package com.islamicmedia.shared.presentation

import com.islamicmedia.shared.util.sanitizeTypedInput

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.islamicmedia.shared.data.api.AnthropicApiClient
import com.islamicmedia.shared.data.api.IsbnLookupService
import com.islamicmedia.shared.data.api.QuranVerseDto
import com.islamicmedia.shared.data.api.createHttpClient
import com.islamicmedia.shared.data.api.serverBaseUrl
import com.islamicmedia.shared.data.auth.SupabaseAuthService
import com.islamicmedia.shared.data.cache.CacheService
import com.islamicmedia.shared.data.api.BatchProgress
import com.islamicmedia.shared.data.api.ScanBatchClient
import com.islamicmedia.shared.data.api.StartBatchOutcome
import com.islamicmedia.shared.data.ocr.SpineScanEvent
import com.islamicmedia.shared.data.ocr.SpineScanner
import com.islamicmedia.shared.data.ocr.TextRecognizer
import com.islamicmedia.shared.data.storage.SettingsStorage
import com.islamicmedia.shared.AppConfig
import com.islamicmedia.shared.domain.model.AnalysisProgress
import com.islamicmedia.shared.domain.model.BulkBatchState
import com.islamicmedia.shared.domain.model.BulkItemStatus
import com.islamicmedia.shared.domain.model.BulkScanItem
import com.islamicmedia.shared.domain.model.BulkScanPhase
import com.islamicmedia.shared.domain.model.BulkResultItem
import com.islamicmedia.shared.domain.model.BulkResultStatus
import com.islamicmedia.shared.domain.model.BulkScanState
import com.islamicmedia.shared.domain.model.AlternativeItem
import com.islamicmedia.shared.domain.model.DisambiguationItem
import com.islamicmedia.shared.domain.model.DisplaySettings
import com.islamicmedia.shared.domain.model.Entitlement
import com.islamicmedia.shared.domain.model.LimitRequestCopy
import com.islamicmedia.shared.domain.model.LimitRequestUiState
import com.islamicmedia.shared.domain.model.errorMessage
import com.islamicmedia.shared.domain.model.ReferralInfo
import com.islamicmedia.shared.domain.model.ReferralInfoCache
import com.islamicmedia.shared.domain.model.ReviewPromptRule
import com.islamicmedia.shared.domain.model.MediaCandidate
import com.islamicmedia.shared.domain.model.MediaPref
import com.islamicmedia.shared.domain.model.MediaType
import com.islamicmedia.shared.domain.model.mediaPrefKey
import com.islamicmedia.shared.domain.model.AnalysisResult
import com.islamicmedia.shared.domain.model.SearchCriteria
import com.islamicmedia.shared.domain.repository.MediaCheckerRepository
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import com.islamicmedia.shared.platform.BackgroundAnalysisManager
import com.islamicmedia.shared.platform.Event
import com.islamicmedia.shared.platform.Param
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import com.islamicmedia.shared.util.IsbnUtils
import com.islamicmedia.shared.util.ReferralCodeFormat
import com.islamicmedia.shared.util.currentTimeMillis
import com.islamicmedia.shared.util.platformName
import com.islamicmedia.shared.util.sanitizeDisplayTitle
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

/**
 * How hard to chase the entitlement after an embedded checkout succeeds.
 *
 * Roughly fifteen seconds. ThriveCart's webhook normally lands within a second or two of the
 * redirect; this is sized to survive a slow one without leaving a paying customer staring at a
 * spinner if the webhook never arrives at all.
 */
private const val CHECKOUT_GRANT_POLL_ATTEMPTS = 10
private const val CHECKOUT_GRANT_POLL_INTERVAL_MS = 1500L

/**
 * Tries per session at attaching a referral code saved from a /r/ link. The auth collector fires
 * on every sign-in and token refresh, and the attach endpoint allows 10 calls an hour, so a code
 * that keeps failing with "error" must not spend them all.
 */
private const val MAX_REFERRAL_FLUSH_ATTEMPTS = 3

/** How old a Kids Mode title must be before it's copied into the parent's Your Analyses (see importKidsScans). */
private const val KIDS_IMPORT_MIN_AGE_MS = 8 * 60_000L

class MediaCheckerViewModel(
    private val backgroundManager: BackgroundAnalysisManager = BackgroundAnalysisManager()
) : ViewModel() {

    private val apiClient = AnthropicApiClient(createHttpClient(), serverBaseUrl())
    private val cacheService = try {
        CacheService(SupabaseAuthService.instance.client)
    } catch (e: Exception) {
        null // Cache unavailable, proceed without caching
    }
    private val repository = MediaCheckerRepository(apiClient, cacheService)
    // Verified-verse cache for the history Quran-correction pass (below). Keyed by the cited
    // reference string; value null means "asked, but not verifiable" → drop the section.
    private val quranVerseCache = mutableMapOf<String, QuranVerseDto?>()
    private val isbnLookup = IsbnLookupService(createHttpClient())
    private val settingsStorage = SettingsStorage()
    private val textRecognizer = TextRecognizer()
    private val spineScanner = SpineScanner(apiClient)
    private val scanBatchClient = ScanBatchClient(apiClient)

    private val _uiState = MutableStateFlow(MediaCheckerUiState())
    val uiState: StateFlow<MediaCheckerUiState> = _uiState.asStateFlow()
    // Shared facade rather than a private tracker instance, so composables that have no
    // ViewModel (the game, the activity tiles) report through the same catalog. See Analytics.kt.
    private val analytics = com.islamicmedia.shared.platform.Analytics

    // ── Subscription billing (see PRD_SUBSCRIPTION_BILLING.md) ──────────────
    private val _entitlement = MutableStateFlow<Entitlement?>(null)
    val entitlement: StateFlow<Entitlement?> = _entitlement.asStateFlow()

    /** Kids Mode — a separate controller that only ever talks to the kids endpoints (see its doc). */
    val kids = KidsModeController(
        scope = viewModelScope,
        repository = repository,
        apiClient = apiClient,
        textRecognizer = textRecognizer,
        entitlement = { _entitlement.value },
        criteria = { _uiState.value.searchCriteria },
        refreshEntitlement = { refreshEntitlement() },
        titles = { titlesIndex }
    )

    /** One-shot external URL (Stripe checkout / billing portal) for the UI to open, then clear. */
    private val _externalUrl = MutableStateFlow<String?>(null)
    val externalUrl: StateFlow<String?> = _externalUrl.asStateFlow()
    fun consumeExternalUrl() { _externalUrl.value = null }

    /** True while a billing URL (checkout / portal) is being fetched — drives a button spinner.
     *  The round-trip hits the server then Stripe, so without this the button looks dead for a
     *  beat (worse on a cold-started server). */
    private val _billingLoading = MutableStateFlow(false)
    val billingLoading: StateFlow<Boolean> = _billingLoading.asStateFlow()

    /** Set when we send the user to ANY Stripe surface (checkout OR the Customer Portal). Whatever
     *  they do there (purchase, cancel, reactivate, switch plan, fix card) reflects via an async
     *  webhook that usually lands just AFTER the redirect/app-switch returns us, so the next
     *  foreground must POLL the entitlement rather than fetch once and lose the race. Without this,
     *  a checkout started OUTSIDE the paywall (e.g. profile's "get credits") wouldn't refresh.
     *  Cleared on the next foreground. */
    private var billingReturnPending = false

    /** Applies a freshly fetched entitlement, firing the funnel's final step on an OBSERVED
     *  TRANSITION into a paying status. Every call site that assigns a fetched [Entitlement] must
     *  route through here — a direct `_entitlement.value = fresh` silently drops real subscribers
     *  from the funnel's conversion rate (that's what happened before this was factored out: the
     *  post-checkout foreground poll assigned entitlement directly, so the funnel never saw
     *  Stripe-checkout conversions land). Two guards, both load-bearing:
     *  - prev != null: on launch the entitlement starts null, so "null → active" is an existing
     *    subscriber being read for the first time, NOT a new purchase. Without this, every
     *    subscriber re-converts on every cold start and the funnel's conversion rate climbs past
     *    100%.
     *  - prev.status != "active": this runs on every foreground/refresh, not just purchases. */
    private fun applyEntitlement(fresh: Entitlement) {
        val prev = _entitlement.value
        if (prev != null && prev.status != "active" && fresh.status == "active") {
            com.islamicmedia.shared.platform.Funnel.track(
                Event.SUBSCRIBED,
                com.islamicmedia.shared.platform.FunnelStep.SUBSCRIBED,
                mapOf(Param.PLAN to (fresh.plan ?: "unknown"))
            )
        }
        _entitlement.value = fresh
        // Subscribing (or cancelling, switching plan…) mid-session changes what the Account screen
        // can manage in-app. The billing summary is otherwise loaded once per app open, so a member
        // who subscribed after launch kept the pre-subscription "not manageable" answer and was
        // sent to Stripe's hosted portal instead of the in-app controls.
        if (prev != null && billingStateChanged(prev, fresh)) refreshAccountDetails()
        // Get the member's referral code onto the device ahead of time, so the "Free Gift" screen
        // opens instantly (the code and link never change; see ReferralInfoCache).
        if (fresh.canRefer) primeReferral()
    }

    // ── Account details (card on file, invoices, email preference) ────────────────────────────
    //
    // Loaded at APP OPEN rather than when the Account screen mounts. Fetching them on mount meant
    // the screen painted with only its static rows and then visibly grew as three network calls
    // landed — the user watched buttons appear under their thumb. Loading them alongside the
    // entitlement means the screen is almost always already populated when it opens, and
    // [accountDetailsLoaded] lets it show a spinner in the rare case it isn't.

    private val _billingSummary = MutableStateFlow<com.islamicmedia.shared.domain.model.BillingSummary?>(null)
    val billingSummary: StateFlow<com.islamicmedia.shared.domain.model.BillingSummary?> = _billingSummary.asStateFlow()

    /** Null means "couldn't find out" — the UI hides the control rather than showing a toggle
     *  whose position would be a guess. */
    private val _emailMarketing = MutableStateFlow<Boolean?>(null)
    val emailMarketing: StateFlow<Boolean?> = _emailMarketing.asStateFlow()

    /** True once the calls have come back, successfully or not. Distinct from [billingSummary]
     *  being null, which is also what a FAILED fetch looks like. */
    private val _accountDetailsLoaded = MutableStateFlow(false)
    val accountDetailsLoaded: StateFlow<Boolean> = _accountDetailsLoaded.asStateFlow()

    /**
     * Fetch the account details. Once per session unless [force]d.
     *
     * The guard matters because this rides along with [refreshEntitlement], which runs on every
     * foreground: without it, every app switch would spend two Stripe round trips re-reading a
     * card that changes once a year.
     */
    fun loadAccountDetails(force: Boolean = false) {
        if (!AppConfig.PAYMENTS_ENABLED) return
        if (_accountDetailsLoaded.value && !force) return
        viewModelScope.launch {
            _billingSummary.value = apiClient.fetchBillingSummary()
            _emailMarketing.value = apiClient.fetchEmailMarketing()
            _accountDetailsLoaded.value = true
        }
    }

    /** Re-read after something changed them — a card update, a reactivation. */
    fun refreshAccountDetails() = loadAccountDetails(force = true)

    /**
     * Opt in or out of marketing email.
     *
     * Optimistic, and reverted on failure: the switch has to move the instant it is touched, but a
     * toggle that stays where you put it while the server refused is worse than one that springs
     * back — it tells the user they have opted out when they have not.
     */
    fun setEmailMarketing(marketing: Boolean) {
        val previous = _emailMarketing.value
        _emailMarketing.value = marketing
        viewModelScope.launch {
            if (!apiClient.setEmailMarketing(marketing)) {
                _emailMarketing.value = previous
                _uiState.update { it.copy(errorMessage = "Couldn't save that preference. Please try again.") }
            }
        }
    }

    /** Refresh credit balances from the server. Safe to call often; no-op when payments are off. */
    fun refreshEntitlement() {
        if (!AppConfig.PAYMENTS_ENABLED) return
        // Rides along deliberately — every existing caller (launch, sign-in, foreground) is also
        // exactly when the account details are worth having ready. Guarded inside, so the extra
        // work happens once.
        loadAccountDetails()
        viewModelScope.launch {
            apiClient.fetchEntitlement()?.let { applyEntitlement(it) }
        }
    }

    /**
     * End a pause early — the "I'm back" action on a paused membership.
     *
     * Stripe bills immediately and the access grant rides in on the resulting paid invoice, which
     * arrives by webhook a moment later. That is why this polls instead of trusting the 200: the
     * server flips the status to active straight away, but `unlimited_until` is only restored once
     * Stripe's invoice lands, and showing "Active" over a still-empty balance is worse than a
     * two-second wait.
     */
    fun resumeSubscription() {
        viewModelScope.launch {
            _billingLoading.value = true
            try {
                if (!apiClient.resumeSubscription()) {
                    _uiState.update { it.copy(errorMessage = "Couldn't resume your membership. Please try again.") }
                    return@launch
                }
                repeat(6) {
                    apiClient.fetchEntitlement()?.let { ent -> applyEntitlement(ent) }
                    if (_entitlement.value?.status == "active") {
                        refreshAccountDetails()
                        return@launch
                    }
                    delay(1500)
                }
            } finally {
                _billingLoading.value = false
            }
        }
    }

    /** Start checkout for a plan ("basic_10" | "pro_30"); the UI opens the returned URL. */
    /**
     * The ThriveCart checkout URL to embed in the paywall, or null when the promotion is off.
     *
     * Pre-filled with the signed-in buyer's email and name, which is the whole reason the in-app
     * path is safe: ThriveCart asks for an email of its own, and a membership granted to a
     * different address than their account leaves them paying with no access. Lives here rather
     * than in either UI so mobile and web can't drift apart on it.
     *
     * Null when the mode is off OR the URL is missing — callers then show the normal Stripe
     * paywall, so a half-configured promotion can never present a dead checkout.
     */
    fun thriveCartCheckoutUrl(): String? {
        val st = _uiState.value
        if (st.paywallMode != com.islamicmedia.shared.domain.model.PricingConfig.MODE_THRIVECART) return null
        val base = st.thriveCartUrl?.takeIf { it.isNotBlank() } ?: run {
            println("[BILLING] paywall_mode=thrivecart but no thrivecart_url — using Stripe paywall")
            return null
        }
        val user = try { SupabaseAuthService.instance.currentUser } catch (_: Exception) { null }
        // AuthUser carries one displayName; ThriveCart wants first/last separately. A split on the
        // first space is good enough for a pre-fill the buyer can still edit.
        val name = user?.displayName?.trim().orEmpty()
        return com.islamicmedia.shared.domain.model.PricingConfig.thriveCartCheckoutUrl(
            baseUrl = base,
            email = user?.email,
            firstName = name.substringBefore(' ').takeIf { it.isNotBlank() },
            lastName = name.substringAfter(' ', "").trim().takeIf { it.isNotBlank() },
        )
    }

    /** Re-check the entitlement after an embedded checkout closes, so a buyer who just paid is
     *  unlocked without restarting the app. The webhook does the granting; this only observes it. */
    fun onCheckoutSheetClosed() {
        billingReturnPending = true
        refreshEntitlement()
    }

    /**
     * The embedded checkout just reached its success page — the purchase went through.
     *
     * Distinct from [onPaywallDismiss], and that distinction is the point: dismissing clears
     * `pendingItem` because closing a paywall means giving up, whereas paying means the blocked
     * search should still run. Treating both as "the sheet closed" silently threw away the search
     * the buyer had just paid to finish.
     *
     * Polls rather than fetching once. Access is granted by ThriveCart's webhook hitting our
     * server, which routinely lands a second or two after the redirect, so a single fetch loses
     * the race. The usual retry loop lives in [onAppForegrounded] and never runs here — the
     * checkout is embedded, so the app never backgrounds.
     */
    fun onCheckoutSucceeded() {
        resumeAfterCheckout = true
        billingReturnPending = false
        viewModelScope.launch {
            repeat(CHECKOUT_GRANT_POLL_ATTEMPTS) { i ->
                apiClient.fetchEntitlement()?.let { applyEntitlement(it) }
                // The collector above reacts to the entitlement change: it drops the paywall and
                // replays the pending search. Stopping here just avoids pointless requests.
                if (canAffordCurrentMode()) return@launch
                if (i < CHECKOUT_GRANT_POLL_ATTEMPTS - 1) delay(CHECKOUT_GRANT_POLL_INTERVAL_MS)
            }
            println("[BILLING] checkout succeeded but no grant after polling — webhook may be delayed")
        }
    }

    /**
     * The success panel's countdown finished: take the checkout off the screen.
     *
     * Deliberately leaves `pendingItem` alone — the search resumes from the entitlement collector
     * once the grant lands, whether that is before or after this point.
     */
    fun onCheckoutSuccessDismissed() {
        _uiState.update { it.copy(showPaywall = false) }
    }

    fun startCheckout(planKey: String) {
        // The ONE begin_checkout call site: web and mobile paywalls and the profile's "get credits"
        // all route here, so the funnel counts each checkout exactly once.
        com.islamicmedia.shared.platform.Funnel.track(
            Event.BEGIN_CHECKOUT,
            com.islamicmedia.shared.platform.FunnelStep.BEGIN_CHECKOUT,
            mapOf(Param.PLAN to planKey)
        )
        // In ThriveCart promotion mode the paywall embeds that page directly (see
        // [thriveCartCheckoutUrl]), so this Stripe path is only reached when the promotion is off —
        // or when it's misconfigured, which deliberately falls through to Stripe rather than
        // leaving a buyer with no way to pay at all.
        viewModelScope.launch {
            _billingLoading.value = true
            try {
                val url = apiClient.createCheckout(planKey, successUrl = com.islamicmedia.shared.platform.platformBillingSuccessUrl())
                if (url != null) {
                    // Arm the foreground poll so the grant is reconciled on return — needed for
                    // checkouts started OUTSIDE the paywall (profile's "get credits"), which have
                    // no showPaywall to drive the poll.
                    billingReturnPending = true
                    _externalUrl.value = url
                } else _uiState.update { it.copy(errorMessage = "Couldn't start checkout. Please try again.") }
            } finally {
                _billingLoading.value = false
            }
        }
    }

    /** Open the Stripe Customer Portal to manage/cancel; the UI opens the returned URL. */
    fun openBillingPortal() {
        analytics.logEvent(Event.OPEN_BILLING_PORTAL)
        viewModelScope.launch {
            _billingLoading.value = true
            try {
                val url = apiClient.fetchBillingPortalUrl(returnUrl = com.islamicmedia.shared.platform.platformBillingSuccessUrl())
                if (url != null) {
                    // Arm the foreground poll so a cancel/reactivate/plan-switch done in the portal
                    // is reconciled on return instead of leaving stale "Renews …" in memory.
                    billingReturnPending = true
                    _externalUrl.value = url
                } else _uiState.update { it.copy(errorMessage = "Couldn't open billing. Please try again.") }
            } finally {
                _billingLoading.value = false
            }
        }
    }

    /**
     * Switch the active subscription to [planKey]. Effective immediately (today) and always
     * prorated: Stripe credits the unused part of the current plan against the new one and
     * charges only the difference now. The change applies server-side via Stripe →
     * customer.subscription.updated webhook, so we poll the entitlement until its plan reflects
     * the switch (or time out).
     */
    fun changePlan(planKey: String) {
        analytics.logEvent(Event.CHANGE_PLAN, mapOf(Param.PLAN to planKey))
        viewModelScope.launch {
            _billingLoading.value = true
            try {
                if (!apiClient.changePlan(planKey)) {
                    _uiState.update { it.copy(errorMessage = "Couldn't change plan. Please try again.") }
                    return@launch
                }
                repeat(6) {
                    apiClient.fetchEntitlement()?.let { ent -> applyEntitlement(ent) }
                    // Done when the new plan is reflected AND the sub is active — the latter also
                    // covers reactivation (canceling → active) when "get credits" reuses this path.
                    val e = _entitlement.value
                    if (e?.plan == planKey && e.status == "active") return@launch
                    delay(1500)
                }
            } finally {
                _billingLoading.value = false
            }
        }
    }

    /** Did a portal action take effect? Status/plan/period are what a cancel, reactivate, plan
     *  switch, or past-due-fix changes — so a difference means the webhook has reconciled. */
    private fun billingStateChanged(before: Entitlement?, after: Entitlement?): Boolean {
        if (after == null) return false
        if (before == null) return true
        return before.status != after.status ||
            before.plan != after.plan ||
            before.currentPeriodEnd != after.currentPeriodEnd
    }

    /** Optimistic UI gate for the selected mode. Unknown (null) → allow; the server enforces. */
    private fun canAffordCurrentMode(): Boolean {
        val e = _entitlement.value ?: return true
        // An active unlimited membership covers both modes. Without one, the user has exactly one
        // free FULL analysis and no free quick check — so this MUST be mode-aware (it used to
        // return isUnlimited for both). The server (consume_credit) enforces the same rule; this
        // just avoids starting a run the server would 402.
        return if (_uiState.value.quickCheck) e.canQuick else e.canFull
    }

    /** Can the user choose between quick and full? Nobody can while
     *  [AppConfig.SEARCH_MODE_CHOICE_ENABLED] is off. With it on: subscriber-only — everyone else
     *  is pinned to FULL so their one free analysis is the good one. Unknown entitlement → not
     *  yet, the clients re-evaluate when it loads. */
    fun canChooseSearchMode(): Boolean =
        AppConfig.SEARCH_MODE_CHOICE_ENABLED &&
            (!AppConfig.PAYMENTS_ENABLED || _entitlement.value?.isUnlimited == true)

    /**
     * Fair-use pre-flight: true, with the daily-limit dialog raised, when the signed-in account is
     * already at its daily limit according to the last entitlement read. Exists to stop the user
     * BEFORE the paid disambiguation search instead of after it. UX only: the server's claim on
     * /api/analyze is the real gate, and [Entitlement.dailyLimitNotice] fails open on anything
     * unknown or stale. Deliberately not gated on PAYMENTS_ENABLED; the cap applies to everyone.
     */
    private fun blockedByDailyLimit(item: DisambiguationItem? = null): Boolean {
        if (!SupabaseAuthService.instance.isLoggedIn) return false
        // Picking a title whose analysis is already RUNNING isn't a new analysis (the server would
        // not count it either): let startAnalysis's [GUARD] take the user to it instead.
        if (item != null && _uiState.value.activeAnalyses.values.any {
                it.status == TaskStatus.RUNNING &&
                    it.title == item.title &&
                    it.item.year == item.year &&
                    it.item.mediaType == item.mediaType
            }) return false
        val notice = _entitlement.value?.dailyLimitNotice() ?: return false
        analytics.logEvent(Event.DAILY_LIMIT_REACHED, mapOf(Param.SOURCE to "preflight"))
        _uiState.update { it.copy(dailyLimitNotice = notice) }
        // Same as the credit gate: re-read, so a stale snapshot (an admin raised the limit, say)
        // can't keep blocking past the next attempt.
        refreshEntitlement()
        return true
    }

    /** The daily-limit dialog's OK. */
    fun dismissLibraryOutOfSearches() {
        _uiState.update { it.copy(showLibraryOutOfSearches = false) }
    }

    /** The "email us" text of the library out-of-searches notice, shown by the dialogs. */
    val libraryOutOfSearchesMessage: String get() = LIBRARY_OUT_OF_SEARCHES_MESSAGE

    fun dismissDailyLimitNotice() {
        // The request form lives inside this dialog, so closing the dialog closes (and forgets) the form too.
        _uiState.update { it.copy(dailyLimitNotice = null, limitRequest = LimitRequestUiState.Hidden) }
    }

    // ── Request additional searches ─────────────────────────────────────────────────────────
    // A button under the weekly-limit dialog's OK opens a form there. The text goes to the server
    // (saved + emailed to support, who answer by hand); nothing is granted automatically. Not offered
    // to library (search pack) accounts, which have their own dialog, and Kids Mode never shows this
    // dialog at all. The form's own text is held by the dialogs, so an error keeps what was typed.

    /** The dialog's "Request additional searches" button: open the form. */
    fun openLimitRequest() {
        _uiState.update {
            if (it.dailyLimitNotice == null || it.limitRequest != LimitRequestUiState.Hidden) it
            else it.copy(limitRequest = LimitRequestUiState.Editing)
        }
    }

    /** The form's Send. Too little text is refused here; the server checks again. One send at a time. */
    fun submitLimitRequest(reason: String) {
        val text = reason.trim()
        if (_uiState.value.limitRequest == LimitRequestUiState.Sending) return
        if (!LimitRequestCopy.canSend(text)) {
            _uiState.update { it.copy(limitRequest = LimitRequestUiState.Error(LimitRequestCopy.TOO_SHORT)) }
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(limitRequest = LimitRequestUiState.Sending) }
            val result = repository.requestMoreSearches(text)
            _uiState.update {
                // Closed (or signed out) while it was on its way: the answer has nowhere to go.
                if (it.limitRequest != LimitRequestUiState.Sending) it
                else it.copy(
                    limitRequest = result.errorMessage()?.let { m -> LimitRequestUiState.Error(m) }
                        ?: LimitRequestUiState.Sent
                )
            }
        }
    }

    /** The form's Cancel: back to the plain limit dialog. */
    fun closeLimitRequest() {
        _uiState.update { it.copy(limitRequest = LimitRequestUiState.Hidden) }
    }

    // Startup fires loadRemoteHistory()/rehydrateHistoryCovers() 2–3× (init +
    // auth-state collector + post-sync). Running them concurrently floods the
    // HTTP/image stack and was corrupting the native heap (Scudo SIGABRT on
    // DefaultDispatch). These guards coalesce overlapping runs: if one is in
    // progress, redundant callers skip (both paths do the same idempotent work).
    private val historySyncMutex = Mutex()
    private val rehydrateMutex = Mutex()
    private val versionSyncMutex = Mutex()

    /** One running coroutine per active analysis, keyed by task id. */
    private val activeJobs = mutableMapOf<String, Job>()
    /** The in-flight disambiguation job, if any. Search-stage only — separate from analyses. */
    private var disambigJob: Job? = null
    /** The in-flight photo title-extraction job, so the user can cancel a slow "Processing…". */
    private var recognizeJob: Job? = null
    private var bulkScanJob: Job? = null
    private var bulkPollJob: Job? = null
    private var bulkItemCounter = 0
    /** The in-flight GET /api/referral/me, so a double open doesn't ask twice and sign-out can
     *  stop a late answer landing in the next account's state. */
    private var referralLoadJob: Job? = null
    /** Guards [flushPendingReferralCode]; see [MAX_REFERRAL_FLUSH_ATTEMPTS]. */
    private var referralFlushInFlight = false
    private var referralFlushAttempts = 0
    private var pendingItem: DisambiguationItem? = null

    /**
     * Set the moment the embedded ThriveCart checkout reaches its success page.
     *
     * Exists because the resume has to outlive the paywall. The grant lands server-side via the
     * webhook, which can arrive after the success panel has already closed the sheet — so the
     * entitlement watcher cannot key off `showPaywall` alone or a purchase that completes a beat
     * too late resumes nothing.
     */
    private var resumeAfterCheckout = false
    /** How the user reached the current analysis (autocomplete / disambiguation / photo_* / isbn /
     *  recommendation rows / upgrade). An INSTANCE field — NOT a startAnalysis param — because the
     *  disambiguation gate stashes pendingItem and replays startAnalysis later (auth/paywall), where a
     *  param would be dropped. Set at each entry leaf, read in startAnalysis, never cleared on read so
     *  auto-retry/resume inherit the original value. Null → "Unknown" in admin. */
    private var pendingSource: String? = null
    /** Photo origin remembered across onImageSelected → onImageCandidateSelected (which is a separate
     *  later call): "photo_camera" (mobile) or "photo_upload" (web). */
    private var pendingImageSource: String = "photo_camera"
    /** Most recent journey id from a DisambiguationComplete event; snapshotted into the task on start. */
    private var lastDisambigJourneyId: String? = null
    private var taskIdCounter = 0L
    /** Set true when a completed analysis makes a store review request due (every 5th completed
     *  analysis on this device, see [ReviewPromptRule]; mobile only, never in Kids Mode). The
     *  request is held until the user CLOSES the analysis detail (see [firePendingReviewPrompt])
     *  so we don't interrupt them mid-read. In-memory only: if the app dies first, the next
     *  completed analysis picks it up, because the rule compares against the count at the last
     *  actual request, not the exact multiple of 5. */
    private var reviewPromptPending = false

    private val MAX_AUTO_RETRIES = 2
    /** Soft cap on simultaneously RUNNING analyses. Past this we show a snackbar and refuse to start. */
    private val SOFT_CAP_RUNNING = 5

    // Auto-retry window: only retry on a connection-class error if the analysis is either very
    // young (< 30s — server probably hasn't started real LLM work, so a retry costs little) or
    // old enough that the result is almost certainly cached (> 90s — retry is a cheap cache hit).
    // The 30–90s middle window is where retries cause real duplicate API spend, so we do not
    // auto-retry there. In that window we instead attempt a resume against the in-flight pipeline
    // (see resumeAttempted + repository.resumeAnalysis).
    private val AUTO_RETRY_EARLY_WINDOW_MS = 30_000L
    private val AUTO_RETRY_LATE_WINDOW_MS = 90_000L

    // Stream-liveness watchdog. The server heartbeats every 15s, so 45s of total silence is three
    // missed beats — the connection is dead, not busy. Deliberately far below OkHttp's 600s socket
    // timeout, which was previously the ONLY thing that noticed a silently-dropped SSE stream and
    // left tasks frozen for ten minutes. Poll every 15s so we react within one beat of the threshold.
    private val STREAM_LIVENESS_TIMEOUT_MS = 45_000L
    private val STREAM_LIVENESS_POLL_MS = 15_000L
    /** Wait before fetching finished write-ups, so units finishing together cost one fetch. */
    private val PARTIAL_REFRESH_DEBOUNCE_MS = 600L

    private val historyJson = Json { ignoreUnknownKeys = true }

    /** In-memory autocomplete catalog (titles index): cached copy loaded on init, then refreshed
     *  from the server. Matched locally on each keystroke; never blocks the UI. */
    private var titlesIndex: List<DisambiguationItem> = emptyList()

    init {
        // Load non-sensitive prefs only. History is NOT loaded here — it must
        // never be shown before auth is confirmed (a restored backup or expired
        // session would otherwise leak a previous account's analyses).
        val savedCriteria = settingsStorage.loadSearchCriteria()
        _uiState.update { it.copy(searchCriteria = savedCriteria) }

        // Display style preference (analysis text size + color).
        _uiState.update { it.copy(displaySettings = settingsStorage.loadDisplaySettings()) }

        // Current hour for time-based home screen greeting.
        val hourOfDay = Instant.fromEpochMilliseconds(currentTimeMillis())
            .toLocalDateTime(TimeZone.currentSystemDefault()).hour
        _uiState.update { it.copy(currentHourOfDay = hourOfDay) }

        // Autocomplete: surface the locally-cached titles index + recent searches instantly,
        // then refresh the index from the server in the background.
        titlesIndex = settingsStorage.loadTitlesIndex()
        _uiState.update { it.copy(recentSearches = settingsStorage.loadRecentSearches()) }
        viewModelScope.launch {
            val fresh = apiClient.getTitlesIndex()
            if (!fresh.isNullOrEmpty()) {
                titlesIndex = fresh
                settingsStorage.saveTitlesIndex(fresh)
                // If the user is already typing, re-rank against the fresh list.
                val q = _uiState.value.searchQuery
                if (q.isNotBlank()) {
                    _uiState.update { it.copy(suggestions = TitleAutocomplete.rank(q, titlesIndex)) }
                }
            }
        }

        // Dynamic paywall pricing (admin-controlled). Public config — fetch immediately, not gated on
        // auth. Keeps the built-in defaults if the fetch fails or the config has no plans.
        viewModelScope.launch {
            apiClient.fetchPricingConfig()?.let { cfg ->
                _uiState.update {
                    it.copy(
                        // Keep the built-in defaults if the config carries no plans, but ALWAYS take
                        // the paywall mode — that's the switch the admin flips for a promotion, and
                        // ignoring it because the plan list happened to be empty would silently
                        // strand buyers on the wrong checkout.
                        pricingPlans = cfg.plans.takeIf { p -> p.isNotEmpty() } ?: it.pricingPlans,
                        paywallMode = cfg.paywallMode,
                        thriveCartUrl = cfg.thriveCartUrl,
                        freeFullLimit = cfg.freeFullLimit,
                    )
                }
            }
        }

        // History visibility is strictly gated on auth state.
        viewModelScope.launch {
            try {
                SupabaseAuthService.instance.authState.collect { state ->
                    when (state) {
                        is com.islamicmedia.shared.data.auth.AuthState.Authenticated -> {
                            // Persist email so it's available even after session expires
                            try {
                                SupabaseAuthService.instance.currentUser?.email?.let { email ->
                                    settingsStorage.saveLastUserEmail(email)
                                }
                            } catch (_: Exception) {}
                            // Now safe to surface the local cache for instant UI,
                            // then reconcile with the server. Prefs are hydrated FIRST so the
                            // cached history is already filtered on its very first frame —
                            // otherwise hidden titles visibly flash in and then disappear.
                            hydrateCachedMediaPrefs()
                            val cached = settingsStorage.loadHistory()
                            if (cached.isNotEmpty()) {
                                _uiState.update { it.copy(history = cached) }
                            }
                            loadRemoteHistory()
                            importKidsScans()
                            loadMediaPrefs()
                            rehydrateHistoryCovers()
                            syncAnalysisVersions()
                            registerPushToken()
                            // A friend's code saved from a /r/ link before they signed up. A
                            // local read and nothing more when none is waiting.
                            flushPendingReferralCode()
                        }
                        is com.islamicmedia.shared.data.auth.AuthState.NotAuthenticated -> {
                            // Signed out / expired / revoked: purge sensitive
                            // local data and clear the UI so nothing leaks.
                            settingsStorage.clearUserData()
                            _uiState.update { it.copy(history = emptyList(), historyLoaded = false) }
                        }
                        is com.islamicmedia.shared.data.auth.AuthState.Loading -> { /* wait */ }
                    }
                }
            } catch (_: Exception) {}
        }

        // A subscription paywall must NEVER stay up once the user can afford the active mode. It can
        // be raised three ways: the optimistic gate (against a possibly-stale local balance), the
        // no-credits backstop (on a server 402 that can be a transient race), and the checkout-return
        // path. We react to BOTH the paywall flipping on AND the balance changing — so we catch both
        // "credits arrive while the paywall is up" and "paywall raised when already affordable" — then
        // dismiss it and resume the blocked search.
        viewModelScope.launch {
            combine(
                _uiState.map { it.showPaywall }.distinctUntilChanged(),
                _entitlement
            ) { _, _ -> }.collect {
                if (canAffordCurrentMode() && (_uiState.value.showPaywall || resumeAfterCheckout)) {
                    resumeAfterCheckout = false
                    _uiState.update { it.copy(showPaywall = false) }
                    pendingItem?.let { item ->
                        pendingItem = null
                        startAnalysis(item)
                    }
                }
            }
        }

        // Resume pending analysis if the app was killed during one (cache hit → instant result)
        val pending = settingsStorage.loadPendingAnalysis()
        if (pending != null) {
            println("[RESUME] Found pending analysis: ${pending.title} (${pending.mediaType}, ${pending.year}, quick=${pending.quickCheck})")
            val item = DisambiguationItem(title = pending.title, mediaType = pending.mediaType, year = pending.year)
            // Small delay to let the UI settle before navigating to progress view
            viewModelScope.launch {
                kotlinx.coroutines.delay(500)
                // Resume in the mode the user actually started. Without this a killed quick check
                // came back in whatever mode the UI defaulted to (often Full), charging a full credit
                // and a full daily-cap slot the user never chose. Null (a record saved by an older
                // build) keeps the old behaviour: the current UI mode.
                startAnalysis(item, forceQuickCheck = pending.quickCheck)
            }
        }
    }

    fun refreshHistory() {
        viewModelScope.launch { loadRemoteHistory(); loadMediaPrefs() }
    }

    fun trackHistoryOpened() {
        analytics.logEvent(Event.VIEW_HISTORY)
    }

    fun trackFoundingMemberClicked() {
        analytics.logEvent(Event.CLICK_JOIN_PROGRAM)
    }

    fun trackFeedbackSubmitted() {
        analytics.logEvent(Event.SUBMIT_FEEDBACK)
    }

    /** Report the screen the user is on. Called from the UI layer, which is the only place that
     *  knows which of the Compose "screens" is currently composed — see Analytics.kt. */
    fun trackScreen(name: String) {
        analytics.screen(name)
    }

    /** A "Your Analyses" filter pill changed. [filter] is the dimension ("media_type"/"verdict"),
     *  [value] the selection or "all" when cleared. Filter state lives in the UI layer (it has to
     *  — see the shared-gridState note in MainScreen), so the UI reports it. */
    fun trackAnalysesFiltered(filter: String, value: String) {
        analytics.logEvent(Event.FILTER_ANALYSES, mapOf(Param.FILTER to filter, Param.FILTER_VALUE to value))
    }

    private suspend fun loadRemoteHistory() {
        // Skip if a sync is already running — startup triggers this 2–3×.
        if (!historySyncMutex.tryLock()) return
        try {
        val email = try { SupabaseAuthService.instance.currentUser?.email } catch (_: Exception) { null }
        if (email == null || cacheService == null) return

        try {
            // ── Phase 1: lightweight metadata (no result_json), all pages ──────────────
            // Gives us the complete unique-title list instantly, regardless of how many
            // analyses the user has. Each page is ~200 events × ~200 bytes ≈ 40 KB.
            val uniqueMeta = cacheService.fetchAllHistoryMeta(email)
            if (uniqueMeta.isEmpty()) return
            println("[HISTORY] Found ${uniqueMeta.size} unique titles for $email")

            // Snapshot the pre-sync local history once so per-batch reconciliation can
            // compare against it without being confused by items we're adding mid-sync.
            val initialLocal = _uiState.value.history.associateBy { it.title.lowercase() }

            fun baseKey(item: HistoryItem): String =
                item.title.replace(Regex("""\s*\(\d{4}\)\s*$"""), "").lowercase().trim() + "::" + item.mediaType.name

            val allRemoteItems = mutableListOf<HistoryItem>()

            // ── Phase 2: fetch result_json in chunks of 50, update UI per chunk ────────
            // The user sees cards appear progressively rather than waiting for the full
            // load. applyVerifiedQuran caches per-reference so later chunks are cheap.
            for (chunk in uniqueMeta.chunked(50)) {
                val chunkIds = chunk.mapNotNull { it.id }
                val fullEvents = cacheService.fetchEventResults(chunkIds)
                val byId = fullEvents.associateBy { it.id }

                val chunkItems = chunk.mapNotNull { meta ->
                    val event = byId[meta.id] ?: return@mapNotNull null
                    try {
                        val result = event.resultJson
                            ?.let { historyJson.decodeFromString<AnalysisResult>(it) }
                            ?: return@mapNotNull null
                        val mediaType = try { MediaType.valueOf(event.mediaType ?: "MOVIES") } catch (_: Exception) { MediaType.MOVIES }
                        val timestamp = try { event.createdAt?.let { parseIsoTimestamp(it) } ?: 0L } catch (_: Exception) { 0L }
                        val sanitizedTitle = (event.title ?: "").sanitizeDisplayTitle()
                        HistoryItem(
                            id = sanitizedTitle,
                            title = sanitizedTitle,
                            mediaType = mediaType,
                            result = result,
                            costUSD = event.costUsd ?: 0.0,
                            timestamp = timestamp,
                            // The server tags KIDZONE checks "kids_…" — the KIDZ label and "see what your child saw".
                            kidScan = meta.searchSource?.startsWith("kids_") == true
                        )
                    } catch (_: Exception) { null }
                }

                // Correct Quran text for this chunk (cached per-reference across chunks).
                val enrichedChunk = applyVerifiedQuran(chunkItems)

                // Reconcile against the initial local snapshot: prefer a newer local version
                // (admin edits) and preserve a locally-resolved cover when the remote is blank.
                val reconciled = enrichedChunk.map { item ->
                    val local = initialLocal[item.title.lowercase()]
                    val base0 = if (local != null && (local.result.version ?: 0L) > (item.result.version ?: 0L)) {
                        item.copy(result = local.result)
                    } else item
                    val base = if (local?.kidScan == true && !base0.kidScan) base0.copy(kidScan = true) else base0
                    if (base.result.coverUrl.isNullOrBlank() && !(local?.result?.coverUrl).isNullOrBlank()) {
                        base.copy(result = base.result.copy(coverUrl = local!!.result.coverUrl))
                    } else base
                }

                allRemoteItems.addAll(reconciled)

                // Append only items not already in the displayed list (earlier batches or
                // pre-existing local items), sorted newest-first.
                _uiState.update { state ->
                    val existing = state.history.map { it.title.lowercase() }.toSet()
                    val fresh = reconciled.filter { it.title.lowercase() !in existing }
                    // A KIDZONE check of a title already in the list: when it's the newer one, it takes the slot
                    // (so it shows the KIDZ label right after the child is done, not only after a reload).
                    val kidNewer = reconciled.filter { it.kidScan }.associateBy { it.title.lowercase() }
                    val merged = state.history.map { old ->
                        val kid = kidNewer[old.title.lowercase()]
                        if (kid != null && !old.kidScan && kid.timestamp >= old.timestamp) kid else old
                    }
                    if (fresh.isEmpty() && merged == state.history) state
                    else state.copy(
                        history = (merged + fresh).sortedByDescending { it.timestamp }
                    )
                }
            }

            // ── After all batches: add local-only items and persist ──────────────────
            // Local-only = titles in the pre-sync local list that never appeared in remote
            // (e.g. analyses done offline before remote tracking, or aged out of the window).
            val remoteTitles = allRemoteItems.map { it.title.lowercase() }.toSet()
            val remoteBaseKeys = allRemoteItems.map { baseKey(it) }.toSet()
            val localOnly = initialLocal.values.filter {
                it.title.lowercase() !in remoteTitles && baseKey(it) !in remoteBaseKeys
            }
            if (localOnly.isNotEmpty()) {
                _uiState.update { state ->
                    val existing = state.history.map { it.title.lowercase() }.toSet()
                    val fresh = localOnly.filter { it.title.lowercase() !in existing }
                    if (fresh.isEmpty()) state
                    else state.copy(
                        history = (state.history + fresh).sortedByDescending { it.timestamp }
                    )
                }
            }

            val finalHistory = _uiState.value.history
            settingsStorage.saveHistory(finalHistory)
            println("[HISTORY] Synced ${allRemoteItems.size} remote + ${localOnly.size} local-only items (${finalHistory.size} total)")
            rehydrateHistoryCovers()
        } catch (e: Exception) {
            println("[HISTORY] Remote history sync failed: ${e.message}")
        }
        } finally {
            // Mark history settled (success, empty, or failure) so the UI can place the credits
            // coachmark only once the "Your Analyses" strip's presence — and thus the content
            // alignment — has stopped changing.
            _uiState.update { it.copy(historyLoaded = true) }
            historySyncMutex.unlock()
        }
    }

    /**
     * On app open (after history loads), cheaply ask the server for the CURRENT version of each
     * cached title and re-fetch any whose version increased. This is how admin edits to concerns,
     * score, or cover art reach EXISTING users — the frozen history copy is the instant first paint,
     * and fresh canonical data swaps in when a change is detected. One lightweight batched probe
     * (only timestamps) gates the heavier re-fetch, which runs only for changed titles. See
     * migration 2026-06-29_analysis_versioning.
     *
     * NOTE: re-fetch uses the user's CURRENT criteria. Concern/score/cover edits are criteria-
     * independent (media_phase1 / media_content) so they always propagate; a Full result whose
     * criteria changed since analysis may not refresh its phase-2 enrichments (acceptable — it
     * keeps the existing full result rather than downgrading).
     */
    private fun syncAnalysisVersions() {
        viewModelScope.launch {
            if (!versionSyncMutex.tryLock()) return@launch
            try {
                val items = _uiState.value.history
                if (items.isEmpty()) return@launch

                val yearRegex = Regex("""\((\d{4})\)\s*$""")
                data class Probe(val item: HistoryItem, val base: String, val year: String?)
                val probes = items.map { item ->
                    val m = yearRegex.find(item.title)
                    val base = if (m != null) item.title.substring(0, m.range.first).trim() else item.title
                    Probe(item, base, m?.groupValues?.get(1))
                }

                val requestBody = buildString {
                    append("""{"items":[""")
                    probes.forEachIndexed { i, p ->
                        if (i > 0) append(",")
                        append("{\"title\":\"").append(p.base.replace("\\", "\\\\").replace("\"", "\\\""))
                        append("\",\"mediaType\":\"").append(p.item.mediaType.name).append("\"")
                        if (p.year != null) append(",\"year\":\"").append(p.year).append("\"")
                        append("}")
                    }
                    append("]}")
                }

                val responseText = apiClient.fetchAnalysisVersions(requestBody) ?: return@launch
                val versionsArr = (historyJson.parseToJsonElement(responseText) as? JsonObject)
                    ?.get("versions") as? JsonArray ?: return@launch
                val serverVersions = mutableMapOf<String, Long>()
                versionsArr.forEach { el ->
                    val o = el as? JsonObject ?: return@forEach
                    val t = (o["title"] as? JsonPrimitive)?.content ?: return@forEach
                    val mt = (o["mediaType"] as? JsonPrimitive)?.content ?: return@forEach
                    val v = (o["version"] as? JsonPrimitive)?.content?.toLongOrNull() ?: 0L
                    serverVersions["${t.lowercase()}::$mt"] = v
                }

                val criteria = _uiState.value.searchCriteria
                val updatedById = mutableMapOf<String, AnalysisResult>()
                for (p in probes) {
                    val serverVersion = serverVersions["${p.base.lowercase()}::${p.item.mediaType.name}"] ?: continue
                    val localVersion = p.item.result.version ?: 0L
                    if (serverVersion <= localVersion) continue
                    val fresh = repository.refetchResult(
                        p.base, p.item.mediaType, criteria, p.year, p.item.result.quickCheck
                    ) ?: continue
                    // Keep an already-resolved cover if the fresh result somehow lost it.
                    val merged = if (fresh.coverUrl.isNullOrBlank() && !p.item.result.coverUrl.isNullOrBlank())
                        fresh.copy(coverUrl = p.item.result.coverUrl) else fresh
                    updatedById[p.item.id] = merged
                }
                if (updatedById.isEmpty()) return@launch

                val newHistory = _uiState.value.history.map { h ->
                    updatedById[h.id]?.let { h.copy(result = it) } ?: h
                }
                _uiState.update { state ->
                    // If the user is currently viewing one of the refreshed titles, update it live.
                    val newViewing = state.viewingHistoryItem?.let { vh ->
                        updatedById[vh.id]?.let { vh.copy(result = it) } ?: vh
                    }
                    state.copy(history = newHistory, viewingHistoryItem = newViewing)
                }
                settingsStorage.saveHistory(newHistory)
                println("[VERSION-SYNC] refreshed ${updatedById.size} stale ${if (updatedById.size == 1) "analysis" else "analyses"}")
            } catch (e: Exception) {
                println("[VERSION-SYNC] failed: ${e.message}")
            } finally {
                versionSyncMutex.unlock()
            }
        }
    }

    /**
     * Replace each history item's Quran Arabic with server-verified Uthmani text (keyed by the
     * cited reference), or drop the reference entirely when it can't be confirmed — so older
     * analyses match the current "authoritative text + ayah medallion, never unverified wording"
     * behavior without a backfill. Results are cached per reference; a network failure leaves the
     * existing (unverified) text untouched rather than blanking everything.
     */
    private suspend fun applyVerifiedQuran(items: List<HistoryItem>): List<HistoryItem> {
        val needed = items
            .flatMap { it.result.concerns }
            .mapNotNull { it.quranReference?.reference }
            .distinct()
            .filter { it !in quranVerseCache }
        if (needed.isNotEmpty()) {
            // null = transport failure → don't cache, leave text untouched this pass. On success,
            // a reference absent from the map is server-confirmed unverifiable → cache null → drop.
            apiClient.resolveQuranReferences(needed)?.let { resolved ->
                for (ref in needed) quranVerseCache[ref] = resolved[ref]
            }
        }
        return items.map { item ->
            val concerns = item.result.concerns.map { concern ->
                val ref = concern.quranReference ?: return@map concern
                if (!quranVerseCache.containsKey(ref.reference)) return@map concern  // call failed → leave as-is
                val verified = quranVerseCache[ref.reference]
                if (verified != null)
                    concern.copy(quranReference = ref.copy(arabic = verified.arabic, reference = verified.reference))
                else
                    concern.copy(quranReference = null)  // unverifiable → hide the section
            }
            item.copy(result = item.result.copy(concerns = concerns))
        }
    }

    /**
     * One-shot backfill for the local history list: pre-coverUrl entries were
     * saved without art, and a server-side backfill can't reach them. For each
     * history item still missing a cover, ask the server (which now has the
     * resolved/backfilled cover_url) and persist it. Idempotent — skips items
     * that already have a cover; reads latest state at apply time so it's safe
     * to run alongside the remote-history sync.
     */
    private fun rehydrateHistoryCovers() {
        viewModelScope.launch {
            // Skip if a rehydration is already running — startup triggers this
            // 2–3×; concurrent runs flooded the native heap (Scudo SIGABRT).
            if (!rehydrateMutex.tryLock()) return@launch
            try {
            val pending = _uiState.value.history.filter { it.result.coverUrl.isNullOrBlank() }
            if (pending.isEmpty()) return@launch
            val resolved = mutableMapOf<String, String>()
            for (item in pending) {
                val url = apiClient.fetchCoverUrl(item.title, item.mediaType.name)
                if (!url.isNullOrBlank()) resolved[item.id] = url
            }
            if (resolved.isEmpty()) return@launch
            _uiState.update { state ->
                state.copy(history = state.history.map { h ->
                    val url = resolved[h.id]
                    if (url != null && h.result.coverUrl.isNullOrBlank()) {
                        h.copy(result = h.result.copy(coverUrl = url))
                    } else h
                })
            }
            settingsStorage.saveHistory(_uiState.value.history)
            println("[HISTORY] Re-hydrated ${resolved.size} cover(s)")
            } finally {
                rehydrateMutex.unlock()
            }
        }
    }

    fun onSearchQueryChanged(query: String) {
        _uiState.update {
            it.copy(
                searchQuery = query,
                suggestions = TitleAutocomplete.rank(query, titlesIndex)
            )
        }
    }

    /**
     * User tapped an autocomplete suggestion. It's already resolved (title + mediaType + year),
     * so this goes straight into the normal selection flow — skipping the Perplexity
     * disambiguation round-trip entirely — after recording the query for "recent searches".
     */
    fun onSuggestionSelected(item: DisambiguationItem) {
        analytics.logEvent(Event.SELECT_AUTOCOMPLETE, mapOf(
            "title" to item.title,
            "media_type" to (item.mediaType ?: "unknown")
        ))
        recordRecentSearch(item.title)
        pendingSource = "autocomplete"
        _uiState.update { it.copy(suggestions = emptyList()) }
        onDisambiguationItemSelected(item)
    }

    /** User tapped a recent-search chip (shown when the box is empty) — re-run that search.
     *  If the query resolves to exactly one local-history entry (whose depth satisfies the current
     *  mode), open it INSTANTLY instead. Ambiguous (multiple versions) or missing → normal search. */
    fun onRecentSearchSelected(query: String) {
        analytics.logEvent(Event.SELECT_RECENT_SEARCH, mapOf("search_term" to query))
        _uiState.update { it.copy(searchQuery = query, suggestions = emptyList()) }
        val cached = _uiState.value.history.filter {
            splitTrailingYear(it.title).first.equals(query.trim(), ignoreCase = true)
        }.singleOrNull()
        if (cached != null && openCachedResult(cached, cached.title, cached.mediaType.name)) return
        onSearch()
    }

    fun removeRecentSearch(query: String) {
        val updated = _uiState.value.recentSearches.filter { it != query }
        _uiState.update { it.copy(recentSearches = updated) }
        settingsStorage.saveRecentSearches(updated)
    }

    /** Prepend [query] to the recent-searches list (deduped, capped at 8) and persist it. */
    private fun recordRecentSearch(query: String) {
        val q = query.trim()
        if (q.isEmpty()) return
        val updated = (listOf(q) + _uiState.value.recentSearches.filter { !it.equals(q, ignoreCase = true) }).take(8)
        _uiState.update { it.copy(recentSearches = updated) }
        settingsStorage.saveRecentSearches(updated)
    }

    fun onSearchCriteriaChanged(criteria: SearchCriteria) {
        // Shape of the filter panel usage — whether people bother with custom concerns and how
        // many age ranges they pick. The concern text itself is user-written, so it stays out.
        analytics.logEvent(Event.CHANGE_SEARCH_CRITERIA, mapOf(
            "has_custom_concerns" to criteria.customConcerns.isNotBlank(),
            "age_range_count" to criteria.childAgeRanges.size
        ))
        _uiState.update { it.copy(searchCriteria = criteria) }
        settingsStorage.saveSearchCriteria(criteria)
    }

    /** Update the "Display style" preference (analysis text size + color) and persist it. */
    fun onDisplaySettingsChanged(settings: DisplaySettings) {
        _uiState.update { it.copy(displaySettings = settings) }
        settingsStorage.saveDisplaySettings(settings)
    }

    fun onQuickCheckChanged(requested: Boolean) {
        // With the choice switched off every search is FULL, so the mode can't be set to quick.
        val quickCheck = requested && AppConfig.SEARCH_MODE_CHOICE_ENABLED
        if (quickCheck != _uiState.value.quickCheck) {
            analytics.logEvent(Event.TOGGLE_QUICK_CHECK, mapOf(Param.ENABLED to quickCheck))
        }
        _uiState.update { it.copy(quickCheck = quickCheck) }
    }

    /** First-run coachmark tour (search/filters/quick-check): per-device "seen" flag. */
    fun getHasSeenCoachmarks(): Boolean = settingsStorage.loadHasSeenCoachmarks()
    fun setCoachmarksSeen() = settingsStorage.saveHasSeenCoachmarks(true)

    /** Post-login credits coachmark (toggle badges): separate per-device "seen" flag. */
    fun getHasSeenCreditsCoachmark(): Boolean = settingsStorage.loadHasSeenCreditsCoachmark()
    fun setCreditsCoachmarkSeen() = settingsStorage.saveHasSeenCreditsCoachmark(true)

    /** Post-payment filters coachmark: fires the first time a user becomes a paid member. */
    fun getHasSeenFiltersCoachmark(): Boolean = settingsStorage.loadHasSeenFiltersCoachmark()
    fun setFiltersCoachmarkSeen() = settingsStorage.saveHasSeenFiltersCoachmark(true)

    /** Result-screen coachmarks (flag button + highlights): fires on first full analysis result with highlights. */
    fun getHasSeenResultCoachmarks(): Boolean = settingsStorage.loadHasSeenResultCoachmarks()
    fun setResultCoachmarksSeen() = settingsStorage.saveHasSeenResultCoachmarks(true)

    /**
     * Re-run the most recent analysis as a full analysis. Used by the "Run full analysis" button
     * shown on quick-check result screens. Uses the currently viewed task or history item as the source.
     */
    fun runFullAnalysisForCurrent() {
        val state = _uiState.value
        // Preserve cover + year from the source so the new task's floating
        // detail can render its art instantly instead of flashing the empty
        // orb backdrop while the server resolves the cover again.
        val sourceCover = state.viewedTask?.coverUrl
            ?: state.viewedTask?.result?.coverUrl
            ?: state.viewingHistoryItem?.result?.coverUrl
        val item = state.viewedTask?.item?.let { it.copy(coverUrl = it.coverUrl ?: sourceCover) }
            ?: state.viewingHistoryItem?.let { h ->
                // Parse "(YYYY)" off the history title so the new task gets a
                // proper year (improves cache hits + display).
                val yearMatch = Regex("""\((\d{4})\)\s*$""").find(h.title)
                val cleanTitle = if (yearMatch != null)
                    h.title.substring(0, yearMatch.range.first).trim()
                else h.title
                DisambiguationItem(
                    title = cleanTitle,
                    year = yearMatch?.groupValues?.get(1),
                    mediaType = h.mediaType.name,
                    coverUrl = sourceCover,
                )
            }
            ?: state.selectedTitle?.let { t ->
                val mt = state.detectedMediaType?.name ?: "MOVIES"
                DisambiguationItem(title = t.substringBeforeLast(" (").trim(), mediaType = mt, coverUrl = sourceCover)
            }
        if (item == null) {
            println("[QUICK_CHECK] Cannot run full analysis: no item available")
            return
        }
        analytics.logEvent(Event.RUN_FULL_ANALYSIS, mapOf(
            Param.TITLE to item.title,
            Param.MEDIA_TYPE to (item.mediaType ?: "unknown")
        ))
        // quickCheck is a search-screen toggle and doesn't drive the floating
        // detail. viewingHistoryItem is intentionally NOT cleared here —
        // startAnalysis's atomic state update clears it in the same emission
        // that sets viewingTaskId, so the floating-detail morph transitions
        // directly from the history's "h-xxx" key to the new taskId in a
        // single crossfade (no null gap → no flash).
        _uiState.update { it.copy(quickCheck = false) }
        pendingSource = "upgrade"
        // This is the quick→full upgrade path → signal the server to refund the quick check's
        // credit (policy A: the user only pays for the full result they keep).
        startAnalysis(item, forceQuickCheck = false, upgradeFromQuick = true)
    }

    /**
     * V2: Retry a single failed Phase 2 enrichment unit. Used when the user taps the retry icon
     * on a concern card with enrichmentStatus="failed". On success, replaces the failed concern
     * in the viewed task's result.
     */
    fun retryConcernUnit(unit: String) {
        val state = _uiState.value
        val viewed = state.viewedTask
        val taskId = viewed?.id

        // After a page reload, the in-memory task is gone but the user is still on the
        // results screen (recovered from cache or via history click). Fall back to reconstructing
        // the item from uiState — both display titles carry "(YEAR)" suffix, so we parse it back
        // out. Without the year, the server's content cache lookup misses and retry returns
        // precondition_missing.
        val yearRegex = Regex("""\((\d{4})\)\s*$""")
        fun parseDisplayTitle(displayTitle: String, mediaTypeName: String): DisambiguationItem {
            val yearMatch = yearRegex.find(displayTitle)
            val cleanTitle = if (yearMatch != null)
                displayTitle.substring(0, yearMatch.range.first).trim()
            else displayTitle
            return DisambiguationItem(
                title = cleanTitle,
                mediaType = mediaTypeName,
                year = yearMatch?.groupValues?.get(1)
            )
        }

        val item = viewed?.item
            ?: state.viewingHistoryItem?.let { h ->
                parseDisplayTitle(h.title, h.mediaType.name)
            }
            ?: state.selectedTitle?.let { displayTitle ->
                val mt = state.detectedMediaType?.name ?: "MOVIES"
                parseDisplayTitle(displayTitle, mt)
            }
            ?: run {
                println("[RETRY] No viewed task and no selectedTitle; cannot retry $unit")
                return
            }
        // De-dup: if this unit is already being retried, ignore the click.
        if (viewed != null && unit in viewed.retryingUnits) return
        val criteria = viewed?.criteria ?: state.searchCriteria
        val mediaType = parseMediaType(item.mediaType ?: "movies")

        // Mark this unit as in-flight so the UI can show a spinner immediately.
        if (taskId != null) {
            updateTask(taskId) { it.copy(retryingUnits = it.retryingUnits + unit) }
        }

        viewModelScope.launch {
            try {
                val result = repository.retryConcern(
                    title = item.title,
                    mediaType = mediaType,
                    criteria = criteria,
                    unit = unit,
                    year = item.year,
                    info = item.info
                )
                when (result) {
                    is com.islamicmedia.shared.domain.repository.MediaCheckerRepository.RetryConcernResult.Success -> {
                        // Re-fetch the full result so all fields update consistently.
                        // Simpler than surgically merging the unit's payload into analysisResult.
                        refetchAnalysisResult(taskId, item, criteria)
                    }
                    is com.islamicmedia.shared.domain.repository.MediaCheckerRepository.RetryConcernResult.Failed -> {
                        // Card stays in failed state — no UI change needed beyond brief feedback.
                        analytics.logEvent(Event.RETRY_CONCERN_FAILED, mapOf(
                            "unit" to unit, "error" to result.error.take(100)
                        ))
                    }
                    is com.islamicmedia.shared.domain.repository.MediaCheckerRepository.RetryConcernResult.Error -> {
                        _uiState.update { it.copy(errorMessage = result.message) }
                    }
                }
            } catch (e: Exception) {
                println("[RETRY] Error retrying $unit: ${e.message}")
                _uiState.update { it.copy(errorMessage = "Retry failed. Please try again.") }
            } finally {
                // Clear the in-flight marker regardless of outcome.
                if (taskId != null) {
                    updateTask(taskId) { it.copy(retryingUnits = it.retryingUnits - unit) }
                }
            }
        }
    }

    /** Re-fetch the analysis result via /api/cached-result and update the task's result in state. */
    private suspend fun refetchAnalysisResult(taskId: String?, item: DisambiguationItem, criteria: SearchCriteria) {
        if (taskId == null) return
        val mediaType = parseMediaType(item.mediaType ?: "movies")
        val quickCheck = _uiState.value.activeAnalyses[taskId]?.quickCheck ?: _uiState.value.quickCheck
        try {
            // Use the same flow as analyze() but the server should hit cache and return immediately.
            // Listen only for the AnalysisComplete event to update the result.
            repository.analyze(
                title = item.title,
                mediaType = mediaType,
                criteria = criteria,
                year = item.year,
                info = item.info,
                author = item.author,
                quickCheck = quickCheck
            ).collect { progress ->
                if (progress is AnalysisProgress.AnalysisComplete) {
                    updateTask(taskId) { it.copy(result = progress.result) }
                }
            }
        } catch (e: Exception) {
            println("[REFETCH] Error: ${e.message}")
        }
    }

    /** [source] records HOW this typed search started: "disambiguation" (plain typed → list pick),
     *  or a "photo_*" value when routed here from a photo scan. */
    fun onSearch(source: String = "disambiguation") {
        returnToBulkResults = false
        val query = _uiState.value.searchQuery.trim()
        if (query.isEmpty()) {
            _uiState.update { it.copy(errorMessage = "Please enter a title or name") }
            return
        }
        // Before the funnel event and the disambiguation call: that search is paid, and a capped
        // account could not analyze whatever it found anyway.
        if (blockedByDailyLimit()) return
        pendingSource = source

        // GA4 recommended event name — "search_term" is the standard param for the search query.
        // Through Funnel so it lands in the admin funnel too: searching is step 2, and on web it's
        // the FIRST thing we can see at all (Firebase gets nothing from the Kobweb build).
        com.islamicmedia.shared.platform.Funnel.track(
            Event.SEARCH,
            com.islamicmedia.shared.platform.FunnelStep.SEARCH,
            mapOf("search_term" to query.take(80))
        )
        recordRecentSearch(query)

        // Only clear search-stage state. Active analyses keep running in the background.
        _uiState.update {
            it.copy(
                isSearching = true,
                errorMessage = null,
                suggestions = emptyList(),
                disambiguationItems = emptyList(),
                showDisambiguation = false,
                selectedTitle = null,
                viewingHistoryItem = null,
                viewingTaskId = null
            )
        }

        disambigJob?.cancel()
        disambigJob = viewModelScope.launch {
            repository.disambiguate(query).collect { progress ->
                handleProgress(progress, taskId = null)
            }
        }
    }

    fun onDisambiguationItemSelected(item: DisambiguationItem) {
        returnToBulkResults = false
        // Already sitting in local history? Show it INSTANTLY — no "analysing" screen, no server
        // round-trip. Only short-circuit when the cached depth satisfies the request: a Full result
        // covers a Quick request, but a Quick result can't stand in for a Full one.
        // ponytail: matches on title/type/year, not current search-filter criteria — same as tapping
        //           the "Your Analyses" strip. Add criteria-aware matching only if users report it.
        findCachedHistoryItem(item)?.let { cached ->
            if (openCachedResult(cached, item.title, item.mediaType ?: "unknown")) return
        }

        // This fires for BOTH paths into a selection: tapping a disambiguation row, and tapping an
        // autocomplete suggestion (onSuggestionSelected routes through here). `source` is what
        // separates them in reports — without it the two are indistinguishable.
        //
        // `position` is only meaningful for the disambiguation list. Autocomplete skips that
        // round-trip entirely, so the list is empty and indexOf returns -1 — omit it rather than
        // send -1, which would otherwise drag the "Result position" metric below zero.
        val items = _uiState.value.disambiguationItems
        val position = items.indexOf(item)
        analytics.logEvent(Event.SELECT_SEARCH_RESULT, buildMap {
            put(Param.TITLE, item.title)
            put(Param.MEDIA_TYPE, item.mediaType ?: "unknown")
            put(Param.SOURCE, pendingSource ?: "unknown")
            if (position >= 0) put("position", position)
        })
        val displayTitle = if (!item.year.isNullOrBlank()) "${item.title} (${item.year})" else item.title
        // Auth gate: must be logged in to analyze
        if (!SupabaseAuthService.instance.isLoggedIn) {
            pendingItem = item
            _uiState.update {
                it.copy(
                    requiresAuth = true,
                    showDisambiguation = false,
                    selectedTitle = displayTitle,
                    detectedMediaType = parseMediaType(item.mediaType)
                )
            }
            return
        }
        // Fair-use gate. After the local-history short-circuit (reopening a result you already
        // have costs nothing) and BEFORE the credit gate: subscribing doesn't lift the daily
        // limit, so a paywall here would sell something that can't help today.
        if (blockedByDailyLimit(item)) return
        // Credit gate: need balance for the selected mode (skipped when payments disabled).
        // Server is authoritative; this proactive check avoids starting a doomed run.
        if (AppConfig.PAYMENTS_ENABLED && !canAffordCurrentMode()) {
            // A library / school account that has used every search is not offered a subscription:
            // it is told to email us (see [LIBRARY_OUT_OF_SEARCHES_MESSAGE]).
            if (_entitlement.value?.isLibrary == true) {
                _uiState.update { it.copy(showPaywall = false, showDisambiguation = false, showLibraryOutOfSearches = true) }
                return
            }
            pendingItem = item
            analytics.logEvent(Event.PAYWALL_SHOWN, mapOf(Param.SOURCE to "out_of_credits"))
            _uiState.update {
                it.copy(
                    showPaywall = true,
                    showDisambiguation = false,
                    selectedTitle = displayTitle,
                    detectedMediaType = parseMediaType(item.mediaType)
                )
            }
            refreshEntitlement()
            return
        }
        startAnalysis(item)
    }

    /**
     * Entry point for a scanned book barcode. Validates the ISBN checksum locally
     * (rejects misreads before spending a lookup), resolves metadata, and surfaces
     * a confirm card. The user confirms to start the analysis — we never silently
     * analyze a possibly-wrong book.
     */
    fun onIsbnScanned(rawBarcode: String) {
        if (!IsbnUtils.isValid(rawBarcode)) {
            _uiState.update {
                it.copy(errorMessage = "That barcode isn't a valid book ISBN. Try scanning the barcode on the back cover.")
            }
            return
        }
        analytics.logEvent(Event.ISBN_SCANNED)
        _uiState.update { it.copy(isResolvingIsbn = true, errorMessage = null, pendingBook = null) }
        viewModelScope.launch {
            val meta = isbnLookup.resolve(rawBarcode)
            if (meta == null) {
                _uiState.update {
                    it.copy(
                        isResolvingIsbn = false,
                        errorMessage = "Couldn't find that book by its ISBN. Try searching by title instead."
                    )
                }
            } else {
                _uiState.update { it.copy(isResolvingIsbn = false, pendingBook = meta) }
            }
        }
    }

    /** User confirmed the scanned book on the confirm card — proceed into analysis. */
    fun onConfirmScannedBook() {
        val book = _uiState.value.pendingBook ?: return
        analytics.logEvent(Event.ISBN_CONFIRMED, mapOf("title" to book.title))
        pendingSource = "isbn"
        _uiState.update { it.copy(pendingBook = null) }
        onDisambiguationItemSelected(
            DisambiguationItem(
                title = book.title,
                year = book.year,
                mediaType = MediaType.BOOKS.name,
                coverUrl = book.coverUrl,
                author = book.authorLine.ifBlank { null }
            )
        )
    }

    /** User rejected the scanned book — drop it and let them search/scan again. */
    fun onDismissScannedBook() {
        _uiState.update { it.copy(pendingBook = null, isResolvingIsbn = false) }
    }

/**
     * Start a new analysis or re-attempt an existing one.
     *
     * - When `existingTaskId` is null: creates a new AnalysisTask, soft-cap-checks against
     *   `SOFT_CAP_RUNNING`, and (if not blocked) launches the SSE collection.
     * - When `existingTaskId` is non-null (auto-retry / resume path): reuses the same task id,
     *   bumps retryCount, and relaunches the SSE collection without the soft-cap check.
     *
     * `forceQuickCheck` overrides the user's current quickCheck toggle — used by
     * `runFullAnalysisForCurrent` to upgrade a Quick result to a Full one.
     */
    private fun startAnalysis(
        item: DisambiguationItem,
        existingTaskId: String? = null,
        forceQuickCheck: Boolean? = null,
        upgradeFromQuick: Boolean = false
    ) {
        val state = _uiState.value
        val mediaType = parseMediaType(item.mediaType)
        val displayTitle = if (!item.year.isNullOrBlank()) "${item.title} (${item.year})" else item.title

        // If a fresh start (not a retry) and an analysis with the same key is already RUNNING,
        // don't create a duplicate — just take the user to the existing one.
        if (existingTaskId == null) {
            val existing = state.activeAnalyses.values.firstOrNull {
                it.status == TaskStatus.RUNNING &&
                    it.title == item.title &&
                    it.item.year == item.year &&
                    it.item.mediaType == item.mediaType
            }
            if (existing != null) {
                println("[GUARD] Same-key analysis already running — viewing existing task ${existing.id}")
                // Clear viewingHistoryItem in the same emission so the floating
                // detail's targetState transitions directly from "h-xxx" to the
                // existing taskId (no null gap → single AnimatedContent crossfade).
                _uiState.update { it.copy(viewingTaskId = existing.id, viewingHistoryItem = null, showDisambiguation = false) }
                return
            }
            // Soft cap on concurrently running analyses.
            if (state.runningAnalysisCount >= SOFT_CAP_RUNNING) {
                _uiState.update { it.copy(showSoftCapWarning = true, showDisambiguation = false) }
                return
            }
        }

        val nowMs = currentTimeMillis()
        val taskId = existingTaskId ?: nextTaskId()
        val existingTask = state.activeAnalyses[taskId]

        // For new tasks, take settings from current UI state. For retries, preserve the original
        // task's settings so a connection blip can't accidentally upgrade Quick → Full (or change
        // criteria) just because the UI has moved on.
        val criteria = existingTask?.criteria ?: state.searchCriteria
        // A NEW search can only be quick while the quick/full choice exists; a retry or resume keeps
        // the mode its analysis was started in.
        val quickCheck = forceQuickCheck ?: existingTask?.quickCheck
            ?: (AppConfig.SEARCH_MODE_CHOICE_ENABLED && state.quickCheck)

        // Persist so a single in-flight analysis can resume if the app is killed, in the SAME mode.
        // (Multi-task cross-restart resume is out of scope; we persist the most recent start.)
        settingsStorage.savePendingAnalysis(item.title, item.mediaType ?: "MOVIES", item.year, quickCheck)

        val journeyId = existingTask?.journeyId ?: lastDisambigJourneyId
        // A fresh handle per RUN (a retry starts a new server pipeline, so it needs its own).
        // Never reuse journeyId for this: it's null for every entry point that skips
        // disambiguation, which is exactly how cancel silently did nothing.
        val cancelToken = "ct-${currentTimeMillis()}-${kotlin.random.Random.nextLong()}"
        // Snapshot the entry-method now (stable for the background coroutine below); a rapid second
        // search must not retag this one. Retries inherit whatever the last leaf set.
        val searchSource = pendingSource

        // Logged here rather than at the top of the function so it reports the RESOLVED mode and
        // entry point (a retry inherits the original task's, not whatever the UI shows now).
        // `source` mirrors the admin dashboard's "Searched Via" so the two agree.
        analytics.logEvent(Event.START_ANALYSIS, mapOf(
            Param.TITLE to item.title,
            Param.MEDIA_TYPE to (item.mediaType ?: "unknown"),
            Param.QUICK_CHECK to quickCheck,
            Param.SOURCE to (searchSource ?: "unknown")
        ))

        val task = existingTask?.let { existing ->
            // Reset progress fields for retry while preserving cumulative metadata.
            existing.copy(
                status = TaskStatus.RUNNING,
                progressSteps = emptyList(),
                progressFraction = 0f,
                maxProgressFraction = 0f,
                phase1PartialResult = null,
                phase2UnitsDone = 0,
                phase2UnitsTotal = 0,
                lastEventAtMs = nowMs,
                result = null,
                failedPhase2Units = emptyList(),
                errorMessage = null,
                internalError = null,
                retryCount = existing.retryCount + 1,
                resumeAttempted = false,
                // A retry is a NEW server pipeline — carry its handle, not the dead run's.
                cancelToken = cancelToken
            )
        } ?: AnalysisTask(
            id = taskId,
            title = item.title,
            displayTitle = displayTitle,
            mediaType = mediaType,
            item = item,
            criteria = criteria,
            quickCheck = quickCheck,
            // ISBN-scanned books already carry a cover — show it instantly.
            coverUrl = item.coverUrl,
            startedAtMs = nowMs,
            journeyId = journeyId,
            cancelToken = cancelToken
        )

        _uiState.update {
            it.copy(
                showDisambiguation = false,
                showPaywall = false,
                selectedTitle = displayTitle,
                detectedMediaType = mediaType,
                errorMessage = null,
                activeAnalyses = it.activeAnalyses + (taskId to task),
                viewingTaskId = taskId,
                // Cleared here so runFullAnalysisForCurrent (and any other path that
                // starts a new analysis from a history view) transitions atomically:
                // floatingEntryId moves directly from "h-historyId" to newTaskId in
                // ONE emission. Without this, the two-step update produced a null
                // gap that triggered a close-then-open double AnimatedContent
                // transition (the "card disappears for a bit" flash).
                viewingHistoryItem = null,
            )
        }

        // Resolve the cover up front so the analyzing screen can show art
        // immediately, instead of waiting for the analysis result. Server
        // resolves-on-miss, so even first-time titles get art in ~1–2s.
        if (task.coverUrl.isNullOrBlank()) {
            viewModelScope.launch {
                val url = try {
                    apiClient.fetchCoverUrl(item.title, item.mediaType ?: mediaType.name, item.year)
                } catch (_: Exception) { null }
                if (!url.isNullOrBlank()) {
                    _uiState.update { st ->
                        val t = st.activeAnalyses[taskId] ?: return@update st
                        // Only fill if still empty (don't clobber a server result).
                        if (!t.coverUrl.isNullOrBlank()) return@update st
                        st.copy(
                            activeAnalyses = st.activeAnalyses + (taskId to t.copy(
                                coverUrl = url,
                                item = t.item.copy(coverUrl = url)
                            ))
                        )
                    }
                }
            }
        }

        // Start Live Activity for the most recently launched task (single-track for now).
        if (existingTaskId == null) backgroundManager.startLiveActivity(item.title)

        activeJobs[taskId]?.cancel()
        activeJobs[taskId] = backgroundManager.backgroundScope.launch {
            val flow = repository.analyze(
                item.title,
                mediaType,
                criteria,
                item.year,
                item.info,
                item.author,
                journeyId,
                quickCheck,
                upgradeFromQuick,
                searchSource,
                cancelToken
            )
            collectAnalysisFlow(flow, taskId, item)
        }
    }

    private fun nextTaskId(): String {
        taskIdCounter += 1
        return "${currentTimeMillis()}-${taskIdCounter}"
    }

    /**
     * Collects events from either an analyze() or resumeAnalysis() flow and applies the
     * shared progress / completion / error handling. Factored out so the resume path can
     * drive the same UI updates without duplicating logic.
     */
    private suspend fun collectAnalysisFlow(
        flow: Flow<AnalysisProgress>,
        taskId: String,
        item: DisambiguationItem
    ) {
        var balanceRefreshedOnStart = false
        // Stream-liveness watchdog. Every recovery path below hangs off an Error event, but a mobile
        // SSE connection routinely dies WITHOUT one: the socket stays open as far as the client is
        // concerned and simply goes quiet, so nothing fires until HttpTimeout's 600s — ten minutes of
        // a task frozen mid-progress. (Observed: Kung Fu Panda 3 on dev, server sent `complete` at
        // T+2m30s, client noticed at T+10m.) The server's 15s heartbeat gives us a real liveness
        // signal, so silence past the threshold means the stream is dead, not slow — at which point
        // the result is very likely already sitting in the cache, finished, waiting to be fetched.
        val watchdog = backgroundManager.backgroundScope.launch {
            while (true) {
                delay(STREAM_LIVENESS_POLL_MS)
                val task = _uiState.value.activeAnalyses[taskId] ?: break
                if (task.status != TaskStatus.RUNNING) break
                val quiet = currentTimeMillis() - task.lastEventAtMs
                if (quiet < STREAM_LIVENESS_TIMEOUT_MS) continue
                println("[WATCHDOG] Stream quiet ${quiet}ms for task=$taskId — probing cache")
                val result = repository.fetchCompletedResult(
                    title = item.title, mediaType = task.mediaType, criteria = task.criteria,
                    year = item.year, info = item.info, author = item.author,
                    quickCheck = task.quickCheck
                )
                if (result == null) {
                    // Not finished yet (or unreachable). Reset the clock so the next probe is a full
                    // interval away rather than firing on every poll while the pipeline still runs.
                    updateTask(taskId) { it.copy(lastEventAtMs = currentTimeMillis()) }
                    continue
                }
                println("[WATCHDOG] Recovered completed result for task=$taskId from cache")
                handleProgress(AnalysisProgress.AnalysisComplete(result, 0.0), taskId)
                break
            }
        }
        try {
        flow.collect { progress ->
            // Once this task has completed, ignore any trailing error from the SSE stream tearing
            // down — on mobile the connection routinely drops right AFTER the result has arrived
            // (app suspend, network blip). Left unguarded, that late "connection error" both
            // (a) clobbers the COMPLETE task into an ERROR state, and (b) trips the >90s
            // "almost certainly cached" auto-retry below, which re-runs /api/analyze from cache and
            // logs a DUPLICATE analysis_complete event — the doubled rows in the admin's Recent
            // Analyses, inflated per-user search counts, and duplicate in-app history entries.
            if (progress is AnalysisProgress.Error &&
                _uiState.value.activeAnalyses[taskId]?.status == TaskStatus.COMPLETE) {
                return@collect
            }
            // The server RESERVES the credit before the SSE stream opens, so the first event we receive
            // means the charge has already landed. Refresh the displayed balance now — not only at
            // completion — so a multi-search user sees their credits drop as each search starts, keeping
            // the optimistic "can I afford this?" gate honest. (Server is the source of truth and already
            // prevents over-spend; this just keeps the UI from lagging.)
            if (!balanceRefreshedOnStart) {
                balanceRefreshedOnStart = true
                refreshEntitlement()
            }
            // Liveness stamp for the watchdog above — every frame counts, including heartbeats,
            // which are the only traffic during a long Phase 2.
            updateTask(taskId) { it.copy(lastEventAtMs = currentTimeMillis()) }
            if (progress is AnalysisProgress.Heartbeat) return@collect
            handleProgress(progress, taskId)

            // Update the ongoing progress notification ONLY while backgrounded — in the foreground the
            // in-app UI shows progress, and a tray notification posted there just lingers (it's never
            // tied to the foreground service, so nothing tears it down on completion).
            val total = (_uiState.value.activeAnalyses[taskId]?.totalConcerns ?: 11) + 4
            val (step, _, desc) = progressToStep(progress, total)
            if (step > 0 && _uiState.value.isBackgroundAnalysis) {
                backgroundManager.updateProgress(item.title, step, total, desc)
            }

            when (progress) {
                is AnalysisProgress.AnalysisComplete -> {
                    settingsStorage.clearPendingAnalysis()
                    backgroundManager.endLiveActivity(true)
                    refreshEntitlement() // a credit was consumed — update balances
                    // If this finished a FULL analysis, drop any superseded quick check of the SAME
                    // title still lingering in activeAnalyses (from the "Full analysis available"
                    // upgrade). Two tasks for one title leave a stale shared-element/transition entry
                    // that intermittently swallows taps on the card — removing it keeps one clean card.
                    val finished = _uiState.value.activeAnalyses[taskId]
                    if (finished != null && !finished.quickCheck) {
                        val titleKey = finished.displayTitle.trim().lowercase()
                        val supersededQuick = _uiState.value.activeAnalyses.values
                            .filter { it.id != taskId && it.quickCheck && it.displayTitle.trim().lowercase() == titleKey }
                            .map { it.id }
                        if (supersededQuick.isNotEmpty()) {
                            _uiState.update { st -> st.copy(activeAnalyses = st.activeAnalyses - supersededQuick) }
                        }
                    }
                    // Only notify when the app is actually backgrounded. A foreground user already
                    // sees the result land in the "Your Analyses" strip; a tray notification posted
                    // while they're in the app just lingers (nothing dismisses it unless tapped).
                    if (_uiState.value.isBackgroundAnalysis) {
                        backgroundManager.showCompletionNotification(item.title, true, null)
                    }
                    if (_uiState.value.isBackgroundAnalysis && _uiState.value.runningAnalysisCount == 0) {
                        backgroundManager.stopBackgroundExecution()
                    }
                }
                is AnalysisProgress.Error -> {
                    // A clean failure is refunded server-side (reserve→refund) — refresh so the
                    // returned credit reflects in the UI rather than staying "spent" until the next sync.
                    refreshEntitlement()
                    // The server hands a daily-cap slot back a beat AFTER this frame (in its finally),
                    // so the read above can still see the pre-release count, and the pre-flight would
                    // then refuse the "try again" this error suggests. Read once more after it lands.
                    if (_entitlement.value?.capLimit != null) {
                        viewModelScope.launch { delay(2_500); refreshEntitlement() }
                    }
                    val isConnectionError = progress.message.contains("SSE error", ignoreCase = true) ||
                        progress.message.contains("SSE connection", ignoreCase = true) ||
                        progress.message.contains("connection", ignoreCase = true) ||
                        progress.message.contains("timed out", ignoreCase = true) ||
                        progress.message.contains("network", ignoreCase = true)

                    val task = _uiState.value.activeAnalyses[taskId]
                    val elapsedMs = if (task != null) currentTimeMillis() - task.startedAtMs else 0L
                    val inSafeWindow = elapsedMs < AUTO_RETRY_EARLY_WINDOW_MS || elapsedMs > AUTO_RETRY_LATE_WINDOW_MS
                    val journey = task?.journeyId

                    when {
                        // Safe-window auto-retry — short or long elapsed time means low duplicate-cost risk.
                        isConnectionError && inSafeWindow && task != null && task.retryCount < MAX_AUTO_RETRIES -> {
                            val window = if (elapsedMs < AUTO_RETRY_EARLY_WINDOW_MS) "early" else "late"
                            println("[RETRY] Auto-retrying analysis ${taskId} (attempt ${task.retryCount + 1}/$MAX_AUTO_RETRIES, $window window, elapsed=${elapsedMs}ms)")
                            startAnalysis(task.item, existingTaskId = taskId)
                            return@collect
                        }
                        // Danger-window connection error — instead of starting a duplicate pipeline,
                        // try to attach to the still-running one via /api/resume.
                        isConnectionError && journey != null && task != null && !task.resumeAttempted -> {
                            updateTask(taskId) { it.copy(resumeAttempted = true) }
                            val resumeItem = task.item
                            println("[RESUME] Attempting resume for journey=$journey (elapsed=${elapsedMs}ms)")
                            activeJobs[taskId]?.cancel()
                            activeJobs[taskId] = backgroundManager.backgroundScope.launch {
                                val resumeFlow = repository.resumeAnalysis(
                                    resumeItem.title, task.mediaType, task.criteria,
                                    resumeItem.year, resumeItem.info, resumeItem.author, journey,
                                    task.quickCheck
                                )
                                collectAnalysisFlow(resumeFlow, taskId, resumeItem)
                            }
                            return@collect
                        }
                        isConnectionError -> {
                            println("[RETRY] Skipping retry/resume — elapsed=${elapsedMs}ms, retries=${task?.retryCount}, resumeAttempted=${task?.resumeAttempted}")
                        }
                    }

                    settingsStorage.clearPendingAnalysis()
                    backgroundManager.endLiveActivity(false)
                    // Only notify when backgrounded (see AnalysisComplete above).
                    if (_uiState.value.isBackgroundAnalysis) {
                        backgroundManager.showCompletionNotification(item.title, false, progress.message)
                    }
                    if (_uiState.value.isBackgroundAnalysis && _uiState.value.runningAnalysisCount == 0) {
                        backgroundManager.stopBackgroundExecution()
                    }
                }
                else -> {}
            }
        }
        } finally {
            // Stop the watchdog when the stream ends for ANY reason — completion, error, or the job
            // being cancelled. It lives on backgroundScope (so it survives the app being backgrounded,
            // which is exactly when connections die), and would otherwise outlive its task.
            watchdog.cancel()
        }
    }

    /** The OS review request has been handed to the platform (InAppReviewEffect's onComplete).
     *  Nothing to record here: [firePendingReviewPrompt] already stored the count it fired at. */
    fun onReviewPromptShown() {
        _uiState.update { it.copy(triggerReviewPrompt = false) }
    }

    fun onAuthenticated() {
        _uiState.update { it.copy(requiresAuth = false) }
        val item = pendingItem ?: return
        pendingItem = null
        viewModelScope.launch {
            // Fetch entitlement before re-running the gate so a brand-new user (null entitlement)
            // doesn't slip past the paywall check via the null→true default in canAffordCurrentMode.
            if (AppConfig.PAYMENTS_ENABLED) {
                apiClient.fetchEntitlement()?.let { applyEntitlement(it) }
            }
            // Re-run the gate — auth now passes, subscription checked with fresh entitlement.
            onDisambiguationItemSelected(item)
        }
    }

    fun onSignedOut() {
        activeJobs.values.forEach { it.cancel() }
        activeJobs.clear()
        disambigJob?.cancel()
        disambigJob = null
        pendingItem = null
        referralLoadJob?.cancel()
        referralLoadJob = null
        referralFlushAttempts = 0
        // A queued store-review request belongs to the session that earned it; don't let it fire
        // over the sign-in screen or for the next account.
        reviewPromptPending = false
        _uiState.update { it.copy(triggerReviewPrompt = false) }
        // Drop the previous user's balance so the credits chip clears immediately on sign-out
        // (it's null-guarded in the UI). The next sign-in refetches via refreshEntitlement().
        _entitlement.value = null
        val savedCriteria = _uiState.value.searchCriteria
        _uiState.update {
            MediaCheckerUiState(searchCriteria = savedCriteria)
        }
    }

    fun onSubscribed() {
        val item = pendingItem ?: return
        pendingItem = null
        startAnalysis(item)
    }

    // ── Voucher redemption ──────────────────────────────────────────────────────────────────
    // The "Have a voucher?" modal on the paywall. A successful redemption writes an unlimited
    // window server-side, which makes this indistinguishable from subscribing as far as the rest
    // of the app is concerned — so it reuses the same resume path.

    fun onOpenVoucherModal() {
        analytics.logEvent(Event.VOUCHER_OPENED)
        _uiState.update { it.copy(showVoucherModal = true, voucherError = null) }
    }

    fun onDismissVoucherModal() {
        _uiState.update { it.copy(showVoucherModal = false, voucherError = null, voucherLoading = false) }
    }

    /** Clears the granted-days confirmation once the UI has shown it. */
    fun onVoucherGrantConsumed() {
        _uiState.update { it.copy(voucherGrantedDays = null) }
    }

    /**
     * Redeem [code]. On success the modal closes, the entitlement is refetched, and the analysis
     * the user was blocked on resumes — the same path `onSubscribed` takes, because from here on
     * a voucher window and a subscription are the same thing.
     *
     * Every failure keeps the modal OPEN with an inline message: the user is mid-typo, and
     * closing it would make them re-open and re-type.
     */
    fun redeemVoucher(code: String) {
        // sanitizeTypedInput, not trim: an RTL keyboard (Arabic/Hebrew/Farsi) wraps Latin runs in
        // invisible bidi marks, and trim() leaves those in place — so a code that looks exactly
        // right on screen gets rejected as not_found with nothing for the user to correct.
        val trimmed = code.sanitizeTypedInput()
        if (trimmed.isEmpty()) {
            _uiState.update { it.copy(voucherError = "not_found") }
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(voucherLoading = true, voucherError = null) }
            val (status, days) = apiClient.redeemVoucher(trimmed)
            if (status != "ok") {
                analytics.logEvent(Event.VOUCHER_FAILED, mapOf("reason" to status))
                _uiState.update { it.copy(voucherLoading = false, voucherError = status) }
                return@launch
            }

            // Pull the granted window before touching the UI, so the paywall can't briefly
            // re-evaluate against a stale entitlement and flash back open.
            apiClient.fetchEntitlement()?.let { applyEntitlement(it) }
            analytics.logEvent(Event.VOUCHER_REDEEMED, mapOf("code" to trimmed.uppercase()))

            _uiState.update {
                it.copy(
                    showVoucherModal = false,
                    voucherLoading = false,
                    voucherError = null,
                    showPaywall = false,
                    voucherGrantedDays = days
                )
            }
            // Resume whatever they were blocked on. No-op if they opened the paywall from the
            // profile rather than from a search.
            onSubscribed()
        }
    }

    // ── Referrals ───────────────────────────────────────────────────────────────────────────
    // Two sides, both dark unless the server has REFERRALS_ENABLED:
    //  - the member's own: [loadReferral] fills the "Refer a friend" screen (code, link, counts —
    //    never a friend's address). The menu item shows only when Entitlement.canRefer.
    //  - the friend's: a code typed in the paywall dialog ([applyReferralCode]) or saved from a /r/
    //    link on the web and attached after sign-in ([flushPendingReferralCode]).
    // Attaching only ties the code to the account. The server reads it at checkout itself, so
    // [startCheckout] sends nothing extra. Every word shown lives in ReferralCopy.

    /**
     * Load, or reload, the member's code, link and counts. Call it each time the Refer a friend
     * screen opens: the counts move as friends pay. The previous answer stays in
     * [MediaCheckerUiState.referralInfo] while this runs and after it fails, so the screen should
     * show the error state only when there is nothing to show instead.
     */
    fun loadReferral() {
        // The saved copy first, so the screen paints the code with no wait; the fetch below then
        // refreshes the counts behind it.
        restoreReferralFromCache()
        if (referralLoadJob?.isActive == true) return
        referralLoadJob = viewModelScope.launch {
            _uiState.update { it.copy(referralLoading = true, referralError = null) }
            val info = apiClient.fetchReferral()
            _uiState.update {
                if (info == null) it.copy(referralLoading = false, referralError = "error")
                else it.copy(
                    referralLoading = false,
                    referralError = null,
                    referralInfo = info,
                    // FRESH only — restoreReferralFromCache() above never touches this. Recomputed
                    // on every fresh answer (not just OR'd in) so a friend's code redeemed some
                    // other way, or a first month that has since ended, correctly clears it too.
                    referralRedeemPromptEligible = info.canRedeem && !hasSeenReferralRedeemPrompt(),
                    // Same "fresh only, recomputed every time" rule: a later, different block must
                    // still turn this true even for an account that already acknowledged an earlier
                    // one, and a block that has since aged out server-side must clear it.
                    referralBlockedNoticeEligible = info.referralBlocked &&
                        !hasAcknowledgedReferralBlocked(info.referralBlockedAt),
                )
            }
            if (info != null) saveReferralCache(info)
        }
    }

    /** Whether THIS device has already shown the "Did a friend send you a code?" popup to the
     *  signed-in account on this backend. True (never show) when there's no owner to key a marker
     *  by — canRedeem should never be true while signed out, but this is the safe default either
     *  way. */
    private fun hasSeenReferralRedeemPrompt(): Boolean {
        val owner = referralCacheOwner() ?: return true
        return try { settingsStorage.hasSeenReferralRedeemPrompt(owner) } catch (_: Exception) { true }
    }

    /** Whether THIS device has already acknowledged (tapped OK on) the "your referral was
     *  blocked" notice for this account+backend at [blockedAt]. True (never show) when there's no
     *  owner to key a marker by, or no [blockedAt] to key it by — referralBlocked should never be
     *  true without one, but this is the safe default either way. */
    private fun hasAcknowledgedReferralBlocked(blockedAt: String?): Boolean {
        val owner = referralCacheOwner() ?: return true
        val at = blockedAt ?: return true
        return try { settingsStorage.hasAcknowledgedReferralBlocked(owner, at) } catch (_: Exception) { true }
    }

    /** "email|ENV" for [ReferralInfoCache]: the saved code belongs to one account on one backend. */
    private fun referralCacheOwner(): String? = try {
        ReferralInfoCache.owner(SupabaseAuthService.instance.currentUser?.email, AppConfig.activeEnvironment.name)
    } catch (_: Exception) { null }

    /** Put this account's saved referral answer on screen, if there is one. True when shown. */
    private fun restoreReferralFromCache(): Boolean {
        if (_uiState.value.referralInfo?.isOk == true) return true
        val owner = referralCacheOwner() ?: return false
        val cached = ReferralInfoCache.decode(settingsStorage.loadReferralCache(), owner) ?: return false
        _uiState.update { it.copy(referralInfo = cached) }
        return true
    }

    /** Keep a usable answer; forget the saved one on a definite "not eligible" / "disabled", so a
     *  member who can no longer refer isn't shown a stale code. A failed fetch changes nothing. */
    private fun saveReferralCache(info: ReferralInfo) {
        val owner = referralCacheOwner() ?: return
        when {
            info.isOk -> ReferralInfoCache.encode(owner, info)?.let { settingsStorage.saveReferralCache(it) }
            info.isNotEligible || info.isDisabled -> settingsStorage.saveReferralCache(null)
        }
    }

    /** Called whenever the entitlement says this member can refer: show the saved code, or fetch it
     *  once in the background so it's ready before they open the screen. Also fetches fresh (even
     *  when a cached answer already painted the screen) while this account+backend hasn't been
     *  shown the one-time redeem popup yet — restoreReferralFromCache() never sets
     *  referralRedeemPromptEligible, so relying on the cache hit alone would mean a subscriber whose
     *  first session never got a clear home screen could go every later app open without
     *  [referralRedeemPromptEligible] being recomputed at all. */
    private fun primeReferral() {
        val restored = restoreReferralFromCache()
        if (!restored || !hasSeenReferralRedeemPrompt()) loadReferral()
    }

    /** The Copy button on the Refer a friend screen (the UI does the copying). */
    fun onReferralCodeCopied() {
        analytics.logEvent(Event.REFERRAL_CODE_COPIED)
    }

    /** The Share button, after the share sheet opened. [method] is how, e.g. "share_sheet",
     *  "web_share" or "copy_fallback" (a browser with no navigator.share). */
    fun onReferralShared(method: String) {
        analytics.logEvent(Event.REFERRAL_LINK_SHARED, mapOf(Param.METHOD to method))
    }

    fun onOpenReferralCodeModal() {
        analytics.logEvent(Event.REFERRAL_CODE_OPENED)
        _uiState.update { it.copy(showReferralCodeModal = true, referralCodeError = null) }
    }

    fun onDismissReferralCodeModal() {
        _uiState.update { it.copy(showReferralCodeModal = false, referralCodeError = null, referralCodeLoading = false) }
    }

    /**
     * Attach a friend's [code], typed in the paywall dialog. On "ok" the dialog closes and
     * [MediaCheckerUiState.referralAttachedCode] is set (the paywall's applied chip); the checkout
     * the user starts next carries the code because the server reads it off the account.
     *
     * Every failure keeps the dialog OPEN with [MediaCheckerUiState.referralCodeError] set, as the
     * voucher dialog does: the user is mid-typo.
     */
    fun applyReferralCode(code: String) {
        if (_uiState.value.referralCodeLoading) return
        val source = "typed_paywall"
        analytics.logEvent(Event.REFERRAL_CODE_ENTERED, mapOf(Param.REFERRAL_SOURCE to source))
        // normalize() runs sanitizeTypedInput first (an RTL keyboard wraps a Latin code in invisible
        // bidi marks that trim() leaves in place), then forgives case, spaces, dashes, O/I/L
        // look-alikes and a pasted share link. Null means it can't be a code at all: say so
        // without spending one of the 10 attach calls an hour.
        val canonical = ReferralCodeFormat.normalize(code)
        if (canonical == null) {
            analytics.logEvent(Event.REFERRAL_CODE_FAILED, mapOf(Param.REFERRAL_SOURCE to source, Param.REASON to "not_found"))
            _uiState.update { it.copy(referralCodeError = "not_found") }
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(referralCodeLoading = true, referralCodeError = null) }
            val result = apiClient.attachReferral(canonical, source)
            if (result.status != "ok") {
                analytics.logEvent(Event.REFERRAL_CODE_FAILED, mapOf(Param.REFERRAL_SOURCE to source, Param.REASON to result.status))
                _uiState.update { it.copy(referralCodeLoading = false, referralCodeError = result.status) }
                return@launch
            }
            onReferralAttached(result.code ?: canonical, source)
            _uiState.update { it.copy(showReferralCodeModal = false, referralCodeLoading = false, referralCodeError = null) }
        }
    }

    /**
     * Attach a code saved from a /r/ link (web: `?refcode=` captured at page load, before sign-up)
     * now that someone is signed in. The auth collector calls this on every sign-in, and it is a
     * local read and nothing more when no code is waiting; the UIs may call it too, it's idempotent.
     *
     * The saved code is forgotten on any FINAL verdict, not only "ok": "not_new" (e.g. they already
     * paid on /register with the code on that session), "self", "already_attached", "not_found" and
     * "referrer_ineligible" won't change by retrying. "error" and "rate_limited" keep it for a later
     * sign-in, up to [MAX_REFERRAL_FLUSH_ATTEMPTS] per session. Silent either way: the person didn't
     * type anything, so there's no dialog to put an error in.
     */
    fun flushPendingReferralCode() {
        if (referralFlushInFlight || referralFlushAttempts >= MAX_REFERRAL_FLUSH_ATTEMPTS) return
        val code = settingsStorage.loadPendingReferralCode() ?: return
        referralFlushInFlight = true
        referralFlushAttempts++
        viewModelScope.launch {
            try {
                val result = apiClient.attachReferral(code, "link")
                if (result.isFinal) settingsStorage.savePendingReferralCode(null, 0L)
                if (result.status == "ok") {
                    onReferralAttached(result.code ?: code, "link")
                } else {
                    analytics.logEvent(Event.REFERRAL_CODE_FAILED, mapOf(Param.REFERRAL_SOURCE to "link", Param.REASON to result.status))
                }
            } finally {
                referralFlushInFlight = false
            }
        }
    }

    /** Shared tail of both attach paths once the server said "ok". */
    private fun onReferralAttached(code: String, source: String) {
        // Any code saved from a link is moot now: the first code attached is the one that counts.
        settingsStorage.savePendingReferralCode(null, 0L)
        _uiState.update { it.copy(referralAttachedCode = code) }
        // Through Funnel, not Analytics alone: Firebase is a no-op on the web, where most friends
        // arrive from a shared link.
        com.islamicmedia.shared.platform.Funnel.track(
            Event.REFERRAL_CODE_APPLIED,
            params = mapOf(Param.REFERRAL_SOURCE to source),
        )
    }

    // ── Redeeming a friend's code AFTER subscribing ────────────────────────────────────────────
    // "Got a code from a friend?" on the member's own Refer a friend screen, shown only while
    // referralInfo?.canRedeem == true (a new subscriber, still in their first month, who hasn't
    // already used a code). POST /api/referral/redeem both records the referral and grants the
    // friend's own free month before answering, so a success refreshes the entitlement (the new
    // next-bill date) and reloads referralInfo (can_redeem flips off, the counts move).

    fun onOpenRedeemCodeModal() {
        analytics.logEvent(Event.REFERRAL_REDEEM_OPENED)
        _uiState.update { it.copy(showRedeemCodeModal = true, redeemCodeError = null) }
    }

    fun onDismissRedeemCodeModal() {
        _uiState.update { it.copy(showRedeemCodeModal = false, redeemCodeError = null, redeemCodeLoading = false) }
    }

    /** Clears the one-time success confirmation once the UI has shown it. */
    fun onRedeemSuccessConsumed() {
        _uiState.update { it.copy(redeemSucceeded = false, redeemSuccessNextBill = null) }
    }

    /**
     * Redeem [code] (blank uses whatever code is already attached to the account — see
     * [AnthropicApiClient.redeemReferral]). On "ok" the dialog closes, the entitlement and referral
     * info are refreshed, and [MediaCheckerUiState.redeemSucceeded] is set for a one-time toast.
     *
     * Every failure keeps the dialog OPEN with [MediaCheckerUiState.redeemCodeError] set, as the
     * attach dialog does: the user is mid-typo.
     */
    fun redeemReferralCode(code: String) {
        if (_uiState.value.redeemCodeLoading) return
        analytics.logEvent(Event.REFERRAL_REDEEM_ENTERED)
        viewModelScope.launch {
            _uiState.update { it.copy(redeemCodeLoading = true, redeemCodeError = null) }
            val result = apiClient.redeemReferral(code.takeIf { it.isNotBlank() })
            if (!result.isOk) {
                analytics.logEvent(Event.REFERRAL_REDEEM_FAILED, mapOf(Param.REASON to result.status))
                _uiState.update { it.copy(redeemCodeLoading = false, redeemCodeError = result.status) }
                return@launch
            }
            // Through Funnel, not Analytics alone — same reason as REFERRAL_CODE_APPLIED.
            com.islamicmedia.shared.platform.Funnel.track(Event.REFERRAL_REDEEMED)
            _uiState.update {
                it.copy(
                    showRedeemCodeModal = false,
                    redeemCodeLoading = false,
                    redeemCodeError = null,
                    redeemSucceeded = true,
                    redeemSuccessNextBill = result.nextBill,
                )
            }
            // The reply already carries the new next-bill date; refetch anyway so the entitlement
            // and the "Renews ..." line the rest of the app reads stay in step with Stripe, not
            // just this screen's toast.
            refreshEntitlement()
            loadReferral()
        }
    }

    // ── The proactive "Did a friend send you a code?" popup ───────────────────────────────────
    // Home-screen offer of the same redeem flow above, for a fresh subscriber who hasn't found the
    // Free Gift screen's "Got a code from a friend?" link on their own. See
    // MediaCheckerUiState.referralRedeemPromptEligible for the trigger; MainScreen / Index.kt own
    // deciding WHEN the home screen is clear enough to actually put it on screen.

    /**
     * Call the instant the popup is actually displayed — not merely when
     * [MediaCheckerUiState.referralRedeemPromptEligible] turns true, which can happen while some
     * other screen is up and the popup has to wait for it to clear. Marks this account+backend
     * seen on the device FIRST, so an app kill mid-popup can never show it twice, then logs
     * [Event.REFERRAL_REDEEM_PROMPT_SHOWN].
     */
    fun onReferralRedeemPromptShown() {
        referralCacheOwner()?.let { owner ->
            try { settingsStorage.markReferralRedeemPromptSeen(owner) } catch (_: Exception) { }
        }
        _uiState.update { it.copy(referralRedeemPromptEligible = false) }
        analytics.logEvent(Event.REFERRAL_REDEEM_PROMPT_SHOWN)
    }

    // ── The "your referral was blocked" notice ─────────────────────────────────────────────────
    // Home-screen notice for a member whose OWN referral (as the friend who typed a code) was
    // voided at payment — see MediaCheckerUiState.referralBlockedNoticeEligible for the trigger;
    // MainScreen / Index.kt own deciding WHEN the home screen is clear enough to put it on screen
    // (the same condition as the redeem popup above, and never together with it).

    /**
     * Call when the notice's OK button is tapped: persists the acknowledgement for this
     * (account+backend, block) FIRST — unlike [onReferralRedeemPromptShown], this is on the tap,
     * not on display, since OK is this notice's only action — then clears
     * [MediaCheckerUiState.referralBlockedNoticeEligible] and, because the code that was blocked
     * must no longer look applied, [MediaCheckerUiState.referralAttachedCode] too (the paywall's
     * "Referral code added" chip).
     */
    fun onReferralBlockedNoticeAcknowledged() {
        val blockedAt = _uiState.value.referralInfo?.referralBlockedAt
        val owner = referralCacheOwner()
        if (owner != null && blockedAt != null) {
            try { settingsStorage.markReferralBlockedAcknowledged(owner, blockedAt) } catch (_: Exception) { }
        }
        _uiState.update { it.copy(referralBlockedNoticeEligible = false, referralAttachedCode = null) }
    }

    /** "Enter code" on the popup: opens the very same redeem dialog the Free Gift screen's "Got a
     *  code from a friend?" opens (MediaCheckerUiState.showRedeemCodeModal). */
    fun onReferralRedeemPromptEnterCode() {
        analytics.logEvent(Event.REFERRAL_REDEEM_PROMPT_ENTER)
        _uiState.update { it.copy(showRedeemCodeModal = true, redeemCodeError = null) }
    }

    /** "No thanks" on the popup, or it was dismissed any other way (backdrop tap, back). */
    fun onReferralRedeemPromptDismissed() {
        analytics.logEvent(Event.REFERRAL_REDEEM_PROMPT_DISMISSED)
    }

    fun onPaywallDismiss() {
        analytics.logEvent(Event.PAYWALL_DISMISSED)
        pendingItem = null
        // Closing the paywall by hand is the opposite of paying: nothing should auto-resume later.
        resumeAfterCheckout = false
        // Clear requiresAuth too so dismissing the auth gate returns to search AND a later
        // analysis attempt re-triggers it (the gate is keyed on requiresAuth changing).
        // Subscription-only: dismissing the paywall never starts a search — there's no free one.
        _uiState.update { it.copy(showPaywall = false, requiresAuth = false) }
        // The ThriveCart promotion embeds its checkout IN this paywall, so closing it may mean the
        // user just paid. The grant happens server-side via the webhook, so re-check rather than
        // assume — otherwise a buyer stays behind the paywall until they restart the app.
        if (thriveCartCheckoutUrl() != null) onCheckoutSheetClosed()
        onNewSearch()
    }

    private fun parseMediaType(typeString: String?): MediaType {
        return when (typeString?.lowercase()) {
            "movie", "movies" -> MediaType.MOVIES
            "tv_show", "tv_shows", "tv show", "tv series" -> MediaType.TV_SHOWS
            "game", "games", "video game", "video_game" -> MediaType.GAMES
            "youtube", "youtube channel" -> MediaType.YOUTUBE
            "book", "books" -> MediaType.BOOKS
            else -> MediaType.MOVIES
        }
    }

    /** Cancel the analysis the user is currently viewing. */
    fun onCancel() {
        val state = _uiState.value
        val taskId = state.viewingTaskId ?: return
        // Read BEFORE cancelTask — it removes the task from activeAnalyses.
        val cancelledJourneyId = state.activeAnalyses[taskId]?.journeyId
        cancelTask(taskId)
        // Return the user to the disambiguation list, but ONLY when it is the list this analysis
        // was actually picked from — cancelling "Frozen (2013)" to choose "Frozen II" instead is
        // the point of that flow.
        //
        // Keyed on the journey, not on "is the list non-empty", because `startAnalysis` clears
        // `showDisambiguation` but never `disambiguationItems`. The list therefore outlives its
        // own journey, and the old check happily resurrected a list belonging to a DIFFERENT,
        // earlier search — or to no search at all, for an analysis started from autocomplete, a
        // recommendation card or a deep link, which never had a disambiguation step. Users
        // cancelled and landed on a stale list instead of the main screen.
        //
        // `journeyId` is null for those non-disambiguation entry points, so it is required to be
        // non-null here: a null journey must never match a null `lastDisambigJourneyId` (which is
        // what a disambiguate response with no X-Journey-Id header leaves behind).
        val listBelongsToCancelledTask = cancelledJourneyId != null &&
            cancelledJourneyId == lastDisambigJourneyId &&
            state.disambiguationItems.isNotEmpty()
        _uiState.update {
            it.copy(
                viewingTaskId = null,
                showDisambiguation = listBelongsToCancelledTask,
                // Drop the list outright when we're not going back to it, so no later path can
                // resurrect it either. Keeping it would leave the same stale state lying around
                // for the next caller to trip over.
                disambiguationItems = if (listBelongsToCancelledTask) it.disambiguationItems else emptyList(),
                selectedTitle = null
            )
        }
    }

    /** Cancel an analysis by id without changing the user's view. Used by inline cancel from the panel. */
    fun cancelTask(taskId: String) {
        val state = _uiState.value
        val task = state.activeAnalyses[taskId] ?: return
        analytics.logEvent(Event.CANCEL_ANALYSIS, mapOf(
            "title" to task.displayTitle,
            "step" to (task.progressSteps.lastOrNull()?.query ?: "unknown")
        ))
        // Tell the SERVER to stop first. Dropping the local job only closes our SSE stream — the
        // pipeline is owned by the server's registry and runs to completion (caching the result,
        // charging the credit and pushing "analysis complete") unless it is explicitly cancelled.
        // On backgroundScope (a SupervisorJob scope that outlives the screen) so the request still
        // goes out if this cancel is the last thing the user does before the VM is torn down.
        backgroundManager.backgroundScope.launch {
            try { apiClient.cancelAnalysis(task.cancelToken, task.journeyId) } catch (_: Exception) {}
        }
        activeJobs[taskId]?.cancel()
        activeJobs.remove(taskId)
        if (state.runningAnalysisCount <= 1) {
            // No more in-flight tasks — clear shared resume hooks.
            settingsStorage.clearPendingAnalysis()
            backgroundManager.endLiveActivity(false)
            backgroundManager.stopBackgroundExecution()
            backgroundManager.dismissNotifications()
        }
        _uiState.update { it.copy(activeAnalyses = it.activeAnalyses - taskId) }
    }

    /** Open the paywall from outside a search (e.g. Kids Mode setup, which is members-only). */
    fun showPaywall(source: String) {
        analytics.logEvent(Event.PAYWALL_SHOWN, mapOf(Param.SOURCE to source))
        _uiState.update { it.copy(showPaywall = true, showDisambiguation = false) }
        refreshEntitlement()
    }

    /** Show the detail view for an active task. */
    fun viewTask(taskId: String) {
        val task = _uiState.value.activeAnalyses[taskId] ?: return

        // Paywall-blocked: re-arm pendingItem so subscribing auto-resumes this analysis,
        // then surface the paywall. Don't navigate to the (empty) task detail view.
        if (task.paywallBlocked) {
            pendingItem = task.item
            analytics.logEvent(Event.PAYWALL_SHOWN, mapOf(Param.SOURCE to "blocked_task_reopened"))
            _uiState.update { it.copy(showPaywall = true, showDisambiguation = false) }
            return
        }

        // Auth-blocked: re-trigger the sign-in gate and resume on auth.
        if (task.authBlocked) {
            pendingItem = task.item
            _uiState.update { it.copy(requiresAuth = true, showDisambiguation = false) }
            return
        }

        _uiState.update {
            it.copy(
                viewingTaskId = taskId,
                viewingHistoryItem = null,
                showDisambiguation = false
            )
        }
        if (task.result != null) {
            loadAlternativesFor(task.title, task.mediaType, task.item.year, task.result.author)
            loadCommentsFor(task.title, task.mediaType, task.item.year)
        }
    }

    /** Return from a task's detail view to the search/list view. The task keeps running. */
    fun closeTask() {
        _uiState.update { it.copy(viewingTaskId = null) }
        clearAlternatives()
        clearComments()
        firePendingReviewPrompt()
        // A book opened from a bulk batch goes back to the batch, not to an empty search screen.
        returnToBulkIfNeeded()
    }

    // ── Alternatives / Similar Titles (live) ──────────────────────────────────
    private var altRequestSeq = 0
    private val trailingYearRegex = Regex("""\s*\((\d{4})\)\s*$""")

    /** Split a display title like "Frozen (2013)" into ("Frozen", "2013"); year is null if absent.
     *  History stores the display title (no separate year), so we recover the bare title+year the
     *  server needs to resolve the source row. */
    private fun splitTrailingYear(display: String): Pair<String, String?> {
        val m = trailingYearRegex.find(display)
        return if (m != null) display.removeRange(m.range).trim() to m.groupValues[1]
        else display.trim() to null
    }

    /** Open [cached] from local history instantly if its depth satisfies the current mode (a Full
     *  result covers a Quick request; a Quick result can't stand in for a Full one). Returns true
     *  when it took over, false to let the caller fall through to a fresh analysis. */
    private fun openCachedResult(cached: HistoryItem, title: String, mediaType: String): Boolean {
        if (!_uiState.value.quickCheck && cached.result.quickCheck) return false
        analytics.logEvent(Event.OPEN_CACHED_RESULT, mapOf("title" to title, "media_type" to mediaType))
        onHistoryItemClick(cached)
        return true
    }

    /** A local-history entry that already answers this selection, or null. Matches base title
     *  (case-insensitive) + media type, and year only when both sides carry one. */
    private fun findCachedHistoryItem(item: DisambiguationItem): HistoryItem? {
        val mt = parseMediaType(item.mediaType)
        val wantTitle = item.title.trim()
        return _uiState.value.history.firstOrNull { h ->
            if (h.mediaType != mt) return@firstOrNull false
            val (hBase, hYear) = splitTrailingYear(h.title)
            hBase.equals(wantTitle, ignoreCase = true) &&
                (item.year.isNullOrBlank() || hYear.isNullOrBlank() || hYear == item.year)
        }
    }

    /** Fetch green, same-type recommendations for the result now on screen. Resets first so a
     *  stale list never bleeds across titles; a superseded fetch (fast nav) is discarded via seq. */
    private fun loadAlternativesFor(title: String, mediaType: MediaType, year: String?, author: String? = null) {
        val seq = ++altRequestSeq
        _uiState.update { it.copy(alternatives = emptyList(), islamicAlternatives = emptyList(), authorAlternatives = emptyList(), isLoadingAlternatives = true) }
        viewModelScope.launch {
            val result = apiClient.fetchAlternatives(title, mediaType.name, year, author)
            if (seq != altRequestSeq) return@launch  // superseded by a newer result view
            val items = result?.items ?: emptyList()
            val islamicItems = result?.islamicItems ?: emptyList()
            val authorItems = result?.authorItems ?: emptyList()
            _uiState.update { it.copy(alternatives = items, islamicAlternatives = islamicItems, authorAlternatives = authorItems, isLoadingAlternatives = false) }
            analytics.logEvent(
                if (items.isNotEmpty()) Event.ALTERNATIVES_SHOWN else Event.ALTERNATIVES_EMPTY,
                mapOf(Param.TITLE to title, Param.MEDIA_TYPE to mediaType.name, "count" to items.size)
            )
        }
    }

    private fun clearAlternatives() {
        altRequestSeq++  // discard any in-flight fetch
        _uiState.update { it.copy(alternatives = emptyList(), islamicAlternatives = emptyList(), authorAlternatives = emptyList(), isLoadingAlternatives = false) }
    }

    // ── Your Analyses pin / hide ──────────────────────────────────────────────

    /** Load the caller's pin/hide prefs. Called alongside the history sync so the list can be
     *  filtered/ordered as soon as it renders. */
    private suspend fun loadMediaPrefs() {
        val result = apiClient.fetchMediaPrefs() ?: return
        _uiState.update { state ->
            state.copy(mediaPrefs = result.prefs.associateBy { it.titleKey })
        }
        // Cache so the NEXT launch can filter on the first frame instead of flashing hidden
        // titles in and then removing them once the network call lands.
        settingsStorage.saveMediaPrefs(result.prefs)
    }

    /** Mirror the in-memory prefs into local storage so a reload filters on the first frame. */
    private fun persistPrefsCache() {
        try { settingsStorage.saveMediaPrefs(_uiState.value.mediaPrefs.values.toList()) } catch (_: Exception) {}
    }

    /** Apply the locally cached prefs immediately (synchronously, before any network work) so
     *  the cached-history render is already filtered. Refreshed from the server right after. */
    private fun hydrateCachedMediaPrefs() {
        val cached = try { settingsStorage.loadMediaPrefs() } catch (_: Exception) { emptyList() }
        if (cached.isEmpty()) return
        _uiState.update { it.copy(mediaPrefs = cached.associateBy { p -> p.titleKey }) }
    }

    fun refreshMediaPrefs() {
        viewModelScope.launch { loadMediaPrefs() }
    }

    /** Pin/unpin a title. Applied locally first so the card reorders instantly, then persisted;
     *  a failed write is rolled back so the list never lies about what's stored. */
    fun togglePin(title: String, mediaType: MediaType) {
        val key = mediaPrefKey(title, mediaType)
        val previous = _uiState.value.mediaPrefs[key]
        val nowPinned = previous?.pinnedAt == null
        analytics.logEvent(Event.PIN_ANALYSIS, mapOf(
            Param.TITLE to title,
            Param.MEDIA_TYPE to mediaType.name,
            "pinned" to nowPinned          // false = the user un-pinned
        ))
        val optimistic = (previous ?: MediaPref(titleKey = key)).copy(
            pinnedAt = if (nowPinned) Instant.fromEpochMilliseconds(currentTimeMillis()).toString() else null
        )
        _uiState.update { it.copy(mediaPrefs = it.mediaPrefs + (key to optimistic)) }
        persistPrefsCache()

        viewModelScope.launch {
            val ok = apiClient.setMediaPref(title, mediaType.name, pinned = nowPinned)
            if (!ok) {
                _uiState.update { state ->
                    val restored = state.mediaPrefs.toMutableMap()
                    if (previous == null) restored.remove(key) else restored[key] = previous
                    state.copy(mediaPrefs = restored)
                }
            }
            persistPrefsCache()
        }
    }

    /** Hide a title from the list. Not a delete — the history row stays, and [clearHiddenFor]
     *  brings it back when the user analyzes the title again. */
    fun hideFromList(title: String, mediaType: MediaType) {
        analytics.logEvent(Event.HIDE_ANALYSIS, mapOf(
            Param.TITLE to title,
            Param.MEDIA_TYPE to mediaType.name
        ))
        val key = mediaPrefKey(title, mediaType)
        val previous = _uiState.value.mediaPrefs[key]
        val optimistic = (previous ?: MediaPref(titleKey = key)).copy(hidden = true)
        _uiState.update { it.copy(mediaPrefs = it.mediaPrefs + (key to optimistic)) }
        persistPrefsCache()

        viewModelScope.launch {
            val ok = apiClient.setMediaPref(title, mediaType.name, hidden = true)
            if (!ok) {
                _uiState.update { state ->
                    val restored = state.mediaPrefs.toMutableMap()
                    if (previous == null) restored.remove(key) else restored[key] = previous
                    state.copy(mediaPrefs = restored)
                }
            }
            persistPrefsCache()
        }
    }

    /** Un-hide a title — called when a fresh analysis for it completes, so a previously hidden
     *  card reappears the moment the user searches it again (the documented Hide behaviour). */
    private fun clearHiddenFor(title: String, mediaType: MediaType) {
        val key = mediaPrefKey(title, mediaType)
        if (_uiState.value.mediaPrefs[key]?.hidden != true) return
        _uiState.update { state ->
            val updated = state.mediaPrefs.toMutableMap()
            updated[key] = (updated[key] ?: MediaPref(titleKey = key)).copy(hidden = false)
            state.copy(mediaPrefs = updated)
        }
        persistPrefsCache()
        viewModelScope.launch { apiClient.setMediaPref(title, mediaType.name, hidden = false) }
    }

    // ── Comments / Reactions (shared, public thread + thumbs up/down per title) ──
    private var commentsRequestSeq = 0

    /** Fetch the shared comment thread + reaction tally for the result now on screen. Resets
     *  first so a stale thread never bleeds across titles; a superseded fetch (fast nav) is
     *  discarded via seq, same pattern as [loadAlternativesFor]. */
    private fun loadCommentsFor(title: String, mediaType: MediaType, year: String?) {
        val seq = ++commentsRequestSeq
        _uiState.update { it.copy(comments = emptyList(), likeCount = 0, dislikeCount = 0, myReaction = null, isLoadingComments = true, commentActionError = null) }
        viewModelScope.launch {
            val result = apiClient.fetchComments(title, mediaType.name, year)
            if (seq != commentsRequestSeq) return@launch  // superseded by a newer result view
            _uiState.update {
                it.copy(
                    comments = result?.comments ?: emptyList(),
                    likeCount = result?.likeCount ?: 0,
                    dislikeCount = result?.dislikeCount ?: 0,
                    myReaction = result?.myReaction,
                    isLoadingComments = false
                )
            }
        }
    }

    private fun clearComments() {
        commentsRequestSeq++  // discard any in-flight fetch
        _uiState.update { it.copy(comments = emptyList(), likeCount = 0, dislikeCount = 0, myReaction = null, isLoadingComments = false, commentActionError = null) }
    }

    /** Post a new comment to the thread for the result now on screen. [firstName] is the
     *  signed-in user's own display first name (the caller already has it locally — same value
     *  used for the home-screen greeting), snapshotted onto the comment at post time. */
    fun postComment(title: String, mediaType: MediaType, year: String?, body: String, firstName: String) {
        val trimmed = body.trim()
        if (trimmed.isBlank()) return
        // Comment bodies are user-written and stay out of analytics — only that one was posted.
        analytics.logEvent(Event.POST_COMMENT, mapOf(
            Param.TITLE to title,
            Param.MEDIA_TYPE to mediaType.name
        ))
        _uiState.update { it.copy(isSubmittingComment = true, commentActionError = null) }
        viewModelScope.launch {
            val comment = apiClient.postComment(title, mediaType.name, year, trimmed, firstName)
            _uiState.update {
                if (comment != null) it.copy(comments = it.comments + comment, isSubmittingComment = false)
                else it.copy(isSubmittingComment = false, commentActionError = "Couldn't post your comment. Try again.")
            }
        }
    }

    /** Edit one of the caller's own comments in place (server 403s if it isn't the caller's). */
    fun editComment(commentId: String, body: String) {
        val trimmed = body.trim()
        if (trimmed.isBlank()) return
        analytics.logEvent(Event.EDIT_COMMENT)
        _uiState.update { it.copy(isSubmittingComment = true, commentActionError = null) }
        viewModelScope.launch {
            val ok = apiClient.editComment(commentId, trimmed)
            val nowIso = Instant.fromEpochMilliseconds(currentTimeMillis()).toString()
            _uiState.update { state ->
                state.copy(
                    isSubmittingComment = false,
                    comments = if (!ok) state.comments else state.comments.map {
                        if (it.id == commentId) it.copy(body = trimmed, editedAt = nowIso) else it
                    },
                    commentActionError = if (ok) null else "Couldn't save your edit. Try again."
                )
            }
        }
    }

    /** Delete one of the caller's own comments (server 403s if it isn't the caller's). */
    fun deleteComment(commentId: String) {
        analytics.logEvent(Event.DELETE_COMMENT)
        _uiState.update { it.copy(isSubmittingComment = true, commentActionError = null) }
        viewModelScope.launch {
            val ok = apiClient.deleteComment(commentId)
            _uiState.update { state ->
                state.copy(
                    isSubmittingComment = false,
                    comments = if (ok) state.comments.filterNot { it.id == commentId } else state.comments,
                    commentActionError = if (ok) null else "Couldn't delete that comment. Try again."
                )
            }
        }
    }

    private var reactionRequestSeq = 0

    /** Tap thumbs up/down. [reaction] is 1 (like) or -1 (dislike); tapping the currently-active
     *  reaction again removes it. Updates the tally optimistically (mirroring the server's
     *  toggle/switch/remove logic) so the tap feels instant, then reconciles with the server's
     *  authoritative response — or rolls back to the pre-tap snapshot if the request fails. A
     *  stale response (superseded by a newer tap) is discarded via seq, same pattern as
     *  [loadAlternativesFor]/[loadCommentsFor]. */
    fun setReaction(title: String, mediaType: MediaType, year: String?, reaction: Int) {
        val seq = ++reactionRequestSeq
        val snapshot = _uiState.value
        val prevReaction = snapshot.myReaction
        val prevLike = snapshot.likeCount
        val prevDislike = snapshot.dislikeCount

        var optimisticLike = prevLike
        var optimisticDislike = prevDislike
        val optimisticReaction: Int?
        if (prevReaction == reaction) {
            // Tapping the active reaction again removes it.
            optimisticReaction = null
            if (reaction == 1) optimisticLike-- else optimisticDislike--
        } else {
            // Fresh reaction or a switch: drop the old vote's count (if any), add the new one's.
            if (prevReaction == 1) optimisticLike-- else if (prevReaction == -1) optimisticDislike--
            if (reaction == 1) optimisticLike++ else optimisticDislike++
            optimisticReaction = reaction
        }
        // Report the resulting state, not the button pressed, so "removed" is distinguishable
        // from a thumbs-down (both arrive here as reaction = -1).
        analytics.logEvent(Event.REACT_TO_ANALYSIS, mapOf(
            Param.TITLE to title,
            Param.MEDIA_TYPE to mediaType.name,
            Param.REACTION to when (optimisticReaction) {
                1 -> "like"
                -1 -> "dislike"
                else -> "removed"
            }
        ))
        _uiState.update {
            it.copy(
                likeCount = optimisticLike.coerceAtLeast(0),
                dislikeCount = optimisticDislike.coerceAtLeast(0),
                myReaction = optimisticReaction
            )
        }

        viewModelScope.launch {
            val result = apiClient.setReaction(title, mediaType.name, year, reaction)
            if (seq != reactionRequestSeq) return@launch  // superseded by a newer tap
            if (result != null) {
                _uiState.update { it.copy(likeCount = result.likeCount, dislikeCount = result.dislikeCount, myReaction = result.myReaction) }
            } else {
                // Roll back to the pre-tap snapshot on failure.
                _uiState.update { it.copy(likeCount = prevLike, dislikeCount = prevDislike, myReaction = prevReaction) }
            }
        }
    }

    /** Tapping an "Alternatives" / "Similar Titles" card → run a fresh analysis for it. Reuses the
     *  standard disambiguation-pick pipeline (auth + credit gate); catalog titles cache-hit fast. */
    fun onAlternativeSelected(item: AlternativeItem, quickCheck: Boolean? = null) {
        analytics.logEvent(Event.ALTERNATIVES_SELECTED, mapOf(
            "title" to item.title,
            "media_type" to item.mediaType,
            "score" to item.score
        ))
        // Which recommendation row was this? Derived from which live list holds the item (and, for the
        // shared alternatives list, the SOURCE title's score — green source ⇒ "Similar Titles", else
        // "Alternatives"), so we don't have to thread a source through the UI onSelect callbacks.
        val state = _uiState.value
        val sourceScore = state.viewedTask?.result?.score ?: state.viewingHistoryItem?.result?.score ?: 0
        pendingSource = when {
            state.authorAlternatives.any { it == item } -> "author_titles"
            state.islamicAlternatives.any { it == item } -> "islamic_picks"
            state.alternatives.any { it == item } -> if (sourceScore >= 8) "similar_titles" else "alternatives"
            else -> "alternatives"
        }
        _uiState.update { it.copy(alternativesScreenTitle = null) }  // leave the standalone screen if we're on it
        // A forced mode is a subscriber-only shortcut: it calls startAnalysis directly, which has no
        // auth/paywall gate (the server's 402 is the only backstop). For everyone else — including a
        // user spending their one free analysis — route through the gated path instead, which runs in
        // the pinned FULL mode and raises the paywall locally when the free analysis is gone.
        if (quickCheck != null && canChooseSearchMode()) {
            // This shortcut skips onDisambiguationItemSelected, so it needs its own fair-use gate.
            val selected = item.toDisambiguationItem()
            if (blockedByDailyLimit(selected)) return
            startAnalysis(selected, forceQuickCheck = quickCheck)
        } else {
            onDisambiguationItemSelected(item.toDisambiguationItem())
        }
    }

    /** Open the standalone "Alternatives to {title}" screen (push deep-link target). Loads a fresh
     *  live list for the title. */
    fun openAlternativesScreen(title: String, mediaType: String, year: String?) {
        val mt = parseMediaType(mediaType)
        analytics.logEvent(Event.OPEN_ALTERNATIVES_SCREEN, mapOf(
            Param.TITLE to title,
            Param.MEDIA_TYPE to mt.name
        ))
        _uiState.update {
            it.copy(
                alternativesScreenTitle = title,
                viewingTaskId = null,
                viewingHistoryItem = null,
                showDisambiguation = false
            )
        }
        loadAlternativesFor(title, mt, year)
    }

    fun closeAlternativesScreen() {
        _uiState.update { it.copy(alternativesScreenTitle = null) }
        clearAlternatives()
    }

    /** If a store review request was queued by a completed analysis (every 5th, see
     *  [ReviewPromptRule]), hand it to the OS now that the user has left the analysis detail.
     *  The UI answers with ONLY the built-in OS review card (InAppReviewEffect) — no custom
     *  dialog, which Apple guideline 5.6.1 forbids. The OS decides whether the card shows.
     *  Mobile only and never in Kids Mode; no-op otherwise. */
    private fun firePendingReviewPrompt() {
        if (!reviewPromptPending) return
        if (platformName == "Web" || kids.state.value.enabled) return
        reviewPromptPending = false
        val count = settingsStorage.loadAnalysisCount()
        // Remember where we asked, so the next request is 5 completed analyses from now.
        settingsStorage.saveLastReviewPromptAt(count)
        analytics.logEvent(Event.REVIEW_PROMPT_REQUESTED, mapOf("analysis_count" to count))
        _uiState.update { it.copy(triggerReviewPrompt = true) }
    }

    /** Dismiss the soft-cap snackbar. */
    fun dismissSoftCapWarning() {
        _uiState.update { it.copy(showSoftCapWarning = false) }
    }

    fun onAppBackgrounded() {
        if (_uiState.value.runningAnalysisCount > 0) {
            val title = _uiState.value.viewedTask?.displayTitle ?: "media"
            backgroundManager.startBackgroundExecution(title)
            _uiState.update { it.copy(isBackgroundAnalysis = true) }
        }
    }

    fun onAppForegrounded() {
        kids.onForeground()
        viewModelScope.launch { importKidsScans() }
        // A resume starts a fresh GA4 session but changes no Compose state, so nothing would
        // re-report where the user is. Re-emit the screen they're already on.
        analytics.reportCurrentScreenAgain()
        if (_uiState.value.isBackgroundAnalysis) {
            backgroundManager.stopBackgroundExecution()
            _uiState.update { it.copy(isBackgroundAnalysis = false) }
        }
        // Back in the app → clear any completion/progress notification that landed while away, so
        // it doesn't linger in the tray while the user is looking at the result in-app.
        backgroundManager.dismissNotifications()
        if (!AppConfig.PAYMENTS_ENABLED) return
        // Returning from a Stripe surface: refresh balances. The webhook that reflects the change is
        // async and often lands just AFTER the redirect/app-switch returns us, so a single fetch can
        // race it and miss. Poll a few times when the paywall is up (checkout grant) OR we just sent
        // the user to the Customer Portal (cancel / reactivate / plan switch); a lone fetch otherwise.
        // Checkout stops once the balance suffices (the reactive collector then dismisses the paywall
        // and resumes the pending search); the portal stops once status/plan/period actually changes.
        val fromBilling = billingReturnPending
        billingReturnPending = false
        val polling = _uiState.value.showPaywall || fromBilling
        viewModelScope.launch {
            val before = _entitlement.value
            repeat(if (polling) 8 else 1) { i ->
                apiClient.fetchEntitlement()?.let { applyEntitlement(it) }
                val done = when {
                    !polling -> true
                    _uiState.value.showPaywall -> canAffordCurrentMode()
                    fromBilling -> billingStateChanged(before, _entitlement.value)
                    else -> true
                }
                if (done) return@launch
                if (i < 7) delay(1500)
            }
        }
    }

    fun requestNotificationPermission(onResult: (Boolean) -> Unit) {
        backgroundManager.requestNotificationPermission(onResult)
    }

    val isNotificationPermissionGranted: Boolean
        get() = backgroundManager.isNotificationPermissionGranted

    fun onDismissError() {
        _uiState.update { it.copy(errorMessage = null, internalError = null) }
    }

    fun submitFeedback(message: String, imageBytes: ByteArray? = null, imageName: String? = null, userEmail: String? = null, userName: String? = null, onComplete: (Boolean) -> Unit = {}) {
        analytics.logEvent(Event.SUBMIT_FEEDBACK)
        viewModelScope.launch {
            val success = try {
                apiClient.sendFeedback(message, imageBytes, imageName, userEmail, userName)
            } catch (_: Exception) {
                false
            }
            onComplete(success)
        }
    }

    fun submitErrorReport(userMessage: String) {
        val state = _uiState.value
        val viewed = state.viewedTask
        // Use current session email, falling back to persisted email from last successful login
        val userEmail = try { SupabaseAuthService.instance.currentUser?.email } catch (_: Exception) { null }
            ?: settingsStorage.loadLastUserEmail()
        // The backend actually in use, NOT the one this build was compiled for. Release builds set
        // AppConfig.forceProd at startup so a store build can never reach DEV — but this line used to
        // print the COMPILE-TIME value, so every store build compiled from a local tree (which
        // defaults to DEV) stamped its reports "Environment: DEV" while talking to PROD. That sent
        // the 2026-08-26 `auth_rejected` investigation to the wrong server's logs. Keep the build
        // flavour too when the two disagree — "which backend" and "debug or release" are both worth
        // knowing, and their disagreement is the normal case for a store build, not a red flag.
        val active = com.islamicmedia.shared.AppConfig.activeEnvironment
        val built = com.islamicmedia.shared.AppConfig.ENVIRONMENT
        val environment = if (active == built) active.name else "${active.name} (built as ${built.name})"
        val platform = getPlatformName()

        viewModelScope.launch {
            try {
                val enrichedDetails = buildString {
                    appendLine(viewed?.internalError ?: state.internalError ?: "No details available")
                    appendLine()
                    appendLine("--- Debug Info ---")
                    appendLine("User: ${userEmail ?: "Not logged in"}")
                    appendLine("Platform: $platform")
                    appendLine("App Version: ${com.islamicmedia.shared.util.appVersion} (${com.islamicmedia.shared.util.appBuildNumber})")
                    appendLine("Environment: $environment")
                    appendLine("Query: ${state.searchQuery}")
                    appendLine("Selected title: ${viewed?.displayTitle ?: state.selectedTitle ?: "None"}")
                    appendLine("Media type: ${viewed?.mediaType?.name ?: state.detectedMediaType?.name ?: "None"}")
                    appendLine("Criteria: ages=${state.searchCriteria.childAgeRanges}, custom=${state.searchCriteria.customConcerns.take(50)}")
                    appendLine("History count: ${state.history.size}")
                    appendLine("Active analyses: ${state.activeAnalyses.size} (${state.runningAnalysisCount} running)")
                }
                apiClient.sendErrorReport(
                    userMessage = userMessage,
                    errorDetails = enrichedDetails,
                    title = viewed?.displayTitle ?: state.selectedTitle ?: state.searchQuery,
                    userEmail = userEmail
                )
            } catch (_: Exception) {
                // Best effort — don't show another error
            }
            _uiState.update { it.copy(errorMessage = null, internalError = null) }
        }
    }

    /**
     * Flag the currently-viewed analysis as problematic. `message` is the user's optional note.
     * Pulls title / media type / score / mode from the viewed task or history item being displayed.
     */
    fun flagAnalysis(message: String, onComplete: (Boolean) -> Unit = {}) {
        analytics.logEvent(Event.FLAG_ANALYSIS)
        val state = _uiState.value
        val historyItem = state.viewingHistoryItem
        val viewed = state.viewedTask
        val result = historyItem?.result ?: viewed?.result
        val title = historyItem?.title ?: viewed?.displayTitle ?: state.selectedTitle ?: state.searchQuery
        val mediaType = (historyItem?.mediaType ?: viewed?.mediaType)?.name
        // Capture the criteria behind this analysis so an admin can re-run faithfully. History
        // items don't carry criteria, so fall back to the current search criteria.
        val criteriaJson = try {
            historyJson.encodeToString(SearchCriteria.serializer(), viewed?.criteria ?: state.searchCriteria)
        } catch (_: Exception) { null }
        viewModelScope.launch {
            val success = try {
                apiClient.submitFlag(
                    title = title,
                    mediaType = mediaType,
                    score = result?.score,
                    quickCheck = result?.quickCheck ?: false,
                    message = message,
                    criteriaJson = criteriaJson,
                )
            } catch (_: Exception) {
                false
            }
            if (success) {
                val key = flagKey(title, historyItem?.mediaType ?: viewed?.mediaType)
                _uiState.update { it.copy(flaggedAnalyses = it.flaggedAnalyses + key) }
            }
            onComplete(success)
        }
    }

    /** Founding member names for the About screen. Founding membership is closed, so this is a
     *  frozen hard-coded list ([FOUNDING_MEMBERS]) — no longer fetched from Supabase. */
    fun fetchFoundingMembers(): List<String> = com.islamicmedia.shared.data.FOUNDING_MEMBERS

    private fun registerPushToken() {
        val token = com.islamicmedia.shared.platform.getPushToken() ?: return
        val platform = com.islamicmedia.shared.platform.getPushPlatform()
        viewModelScope.launch {
            try {
                apiClient.registerDeviceToken(token, platform)
                println("[FCM] Push token registered for $platform")
            } catch (e: Exception) {
                println("[FCM] Failed to register push token: ${e.message}")
            }
        }
    }

    private fun getPlatformName(): String = try {
        when {
            com.islamicmedia.shared.util.isDebugBuild -> "Android (Debug)"
            else -> "Android/iOS"
        }
    } catch (_: Exception) { "Unknown" }

    /** Fetch exactly what the child saw for a Kids Mode check. Called when its detail view opens, so
     *  the flip card has the answer before the parent taps. Never touches Kids Mode itself.
     *  Only for an account KIDZONE is switched on for: otherwise no kids request is made at all. */
    fun preloadKidsPreview(item: HistoryItem) {
        if (!item.kidScan || _entitlement.value?.kidsMode != true) {
            _uiState.update { if (it.kidsPreview == null && !it.kidsFlipped) it else it.copy(kidsPreview = null, kidsFlipped = false) }
            return
        }
        _uiState.update { it.copy(kidsPreview = KidsPreviewState.Loading, kidsFlipped = false) }
        viewModelScope.launch {
            val (bareTitle, year) = splitTrailingYear(item.title)
            val read = try {
                repository.fetchKidsResult(bareTitle, item.mediaType, _uiState.value.searchCriteria, year, null, item.result.author)
            } catch (e: CancellationException) {
                throw e
            } catch (_: Exception) {
                null
            }
            _uiState.update { s ->
                if (s.viewingHistoryItem?.id != item.id) s  // the parent moved on while it loaded
                else s.copy(kidsPreview = if (read is com.islamicmedia.shared.domain.model.KidsCacheRead.Ready) KidsPreviewState.Ready(read.result) else KidsPreviewState.Failed)
            }
            // The server says KIDZONE is off for this account: pick that up, so the preview button goes.
            if (read is com.islamicmedia.shared.domain.model.KidsCacheRead.NotAllowed) refreshEntitlement()
        }
    }

    /** Flip the detail card over. Normally the answer is already loaded; if the first try failed, try again. */
    fun openKidsPreview(item: HistoryItem) {
        if (_entitlement.value?.kidsMode != true) return
        val current = _uiState.value.kidsPreview
        if (current is KidsPreviewState.Ready || current is KidsPreviewState.Loading) {
            _uiState.update { it.copy(kidsFlipped = true) }
        } else {
            preloadKidsPreview(item)
            _uiState.update { it.copy(kidsFlipped = true) }
        }
    }

    /** Flip back to the analysis (the loaded answer is kept). */
    fun closeKidsPreview() {
        _uiState.update { if (it.kidsFlipped) it.copy(kidsFlipped = false) else it }
    }

    /** Leaving the detail view: drop the preview altogether. */
    fun clearKidsPreview() {
        _uiState.update { if (it.kidsPreview == null && !it.kidsFlipped) it else it.copy(kidsPreview = null, kidsFlipped = false) }
    }

    fun onHistoryItemClick(item: HistoryItem) {
        returnToBulkResults = false
        // Display the history item without re-running the search. Clears any active task view.
        val needsHighlights = item.result.tldr.isNullOrEmpty() && !item.result.quickCheck
        analytics.logEvent(Event.OPEN_ANALYSIS_DETAIL, mapOf(
            Param.TITLE to item.title,
            Param.MEDIA_TYPE to item.mediaType.name,
            Param.QUICK_CHECK to item.result.quickCheck
        ))
        _uiState.update {
            it.copy(
                viewingHistoryItem = item,
                viewingTaskId = null,
                selectedTitle = item.title,
                detectedMediaType = item.mediaType,
                showDisambiguation = false,
                isSearching = false,
                disambiguationItems = emptyList(),
                errorMessage = null,
                highlightsLoading = needsHighlights
            )
        }
        preloadKidsPreview(item)
        val (bareTitle, histYear) = splitTrailingYear(item.title)
        loadAlternativesFor(bareTitle, item.mediaType, histYear, item.result.author)
        loadCommentsFor(bareTitle, item.mediaType, histYear)

        // If the stored result has no highlights (older analyses predate the feature),
        // fetch the current cached result in the background and overlay the tldr.
        if (needsHighlights) {
            viewModelScope.launch {
                try {
                    val criteria = _uiState.value.searchCriteria
                    val fresh = repository.refetchResult(bareTitle, item.mediaType, criteria, histYear, quickCheck = true)
                    if (fresh?.tldr?.isNotEmpty() == true) {
                        val updatedItem = item.copy(result = item.result.copy(tldr = fresh.tldr))
                        _uiState.update { state ->
                            if (state.viewingHistoryItem?.id == item.id)
                                state.copy(viewingHistoryItem = updatedItem, highlightsLoading = false)
                            else state
                        }
                    }
                } catch (_: Exception) {}
                finally {
                    _uiState.update { if (it.viewingHistoryItem?.id == item.id) it.copy(highlightsLoading = false) else it }
                }
            }
        }
    }

    /**
     * Pull in server-generated TL;DR highlights for a just-completed FRESH analysis. The server
     * generates them (Haiku) fire-and-forget after emitting `complete`, so the result the client
     * first renders has tldr=null. We poll a few times and, once highlights land, patch them into
     * the live task, the history entry, and the currently-viewed item — no second search needed.
     *
     * The poll runs at quickCheck=FALSE so it reproduces the exact charge key the analysis that
     * just finished was charged under (mode is baked into the key — InFlightRegistry.computeKey),
     * making /api/cached-result an idempotent, FREE re-read of a search the user already paid for.
     * Polling at quickCheck=true — as this used to — computes a *different* key, so the endpoint
     * bills it as a fresh quick check: harmless for a subscriber, but a 402 for a user spending
     * their one free full analysis, which silently cost them their highlights.
     *
     * The criteria-independent quick probe survives as a fallback for the case that motivated it:
     * criteria drifting between the analysis starting and this poll running, which would miss the
     * criteria-keyed cache row. A free user 402s on that fallback and simply gets no highlights —
     * but only after the free path above has already had four chances to land them.
     */
    // ── Filling concern cards as their write-ups land ─────────────────────────────────────────
    // A concern_enriched event names the finished unit only, so the write-up is fetched. Bursts
    // (several units finishing together) collapse into one fetch, and a unit that finishes while a
    // fetch is in flight triggers exactly one more. Touched only from the main thread.
    private val partialRefreshJobs = mutableMapOf<String, Job>()
    private val partialRefreshAgain = mutableSetOf<String>()

    private fun requestPartialRefresh(taskId: String) {
        if (partialRefreshJobs[taskId]?.isActive == true) {
            partialRefreshAgain += taskId
            return
        }
        partialRefreshJobs[taskId] = viewModelScope.launch {
            do {
                partialRefreshAgain -= taskId
                delay(PARTIAL_REFRESH_DEBOUNCE_MS)
                val task = _uiState.value.activeAnalyses[taskId] ?: break
                if (task.status != TaskStatus.RUNNING || task.result != null || task.phase1PartialResult == null) break
                val item = task.item
                val fresh = try {
                    repository.fetchPartialResult(
                        title = item.title, mediaType = task.mediaType, criteria = task.criteria,
                        year = item.year, info = item.info, author = item.author,
                    )
                } catch (e: CancellationException) {
                    throw e
                } catch (e: Exception) {
                    println("[PARTIAL] fetch failed for task=$taskId: ${e.message}")
                    null
                }
                if (fresh != null) mergeFinishedWriteUps(taskId, fresh)
            } while (taskId in partialRefreshAgain)
            partialRefreshJobs -= taskId
        }
    }

    /**
     * Swap every concern whose write-up is finished in [fresh] into the streamed verdict. The verdict
     * itself (score, severities, order) stays exactly as streamed; only card bodies change. Concerns
     * that only exist after Phase 2 (custom concerns) are appended once written.
     */
    private fun mergeFinishedWriteUps(taskId: String, fresh: AnalysisResult) {
        updateTask(taskId) { task ->
            val partial = task.phase1PartialResult
            if (partial == null || task.result != null) return@updateTask task
            val finished = fresh.concerns.filter { it.enrichmentStatus != "pending" }
            val merged = partial.concerns.map { shown ->
                finished.firstOrNull { it.category == shown.category }
                    ?.let { it.copy(instances = it.instances.ifEmpty { shown.instances }) }
                    ?: shown
            } + finished.filter { f -> partial.concerns.none { it.category == f.category } }
            task.copy(phase1PartialResult = partial.copy(
                concerns = merged,
                tldr = partial.tldr?.takeIf { it.isNotEmpty() } ?: fresh.tldr,
            ))
        }
    }

    private fun backfillTldrForCompletedTask(
        taskId: String,
        title: String,
        mediaType: MediaType,
        year: String?,
        historyId: String
    ) {
        viewModelScope.launch {
            _uiState.update { it.copy(highlightsLoading = true) }
            try {
                val criteria = _uiState.value.searchCriteria
                repeat(5) { attempt ->
                    delay(4000L)
                    // Attempts 0–3 re-read under the analysis's own (already-charged) key; the last
                    // one falls back to the criteria-independent quick probe. See the KDoc above.
                    val asQuick = attempt == 4
                    val fresh = repository.refetchResult(title, mediaType, criteria, year, quickCheck = asQuick)
                    val tldr = fresh?.tldr
                    if (!tldr.isNullOrEmpty()) {
                        updateTask(taskId) { t -> t.result?.let { t.copy(result = it.copy(tldr = tldr)) } ?: t }
                        val newHistory = _uiState.value.history.map { h ->
                            if (h.id == historyId) h.copy(result = h.result.copy(tldr = tldr)) else h
                        }
                        _uiState.update { st ->
                            val newViewing = st.viewingHistoryItem?.let { vh ->
                                if (vh.id == historyId) vh.copy(result = vh.result.copy(tldr = tldr)) else vh
                            }
                            st.copy(history = newHistory, viewingHistoryItem = newViewing, highlightsLoading = false)
                        }
                        settingsStorage.saveHistory(newHistory)
                        return@launch
                    }
                }
            } catch (_: Exception) {}
            finally { _uiState.update { it.copy(highlightsLoading = false) } }
        }
    }

    fun onClearHistory() {
        _uiState.update { it.copy(history = emptyList()) }
        settingsStorage.saveHistory(emptyList())
    }

    fun onDeleteHistoryItem(item: HistoryItem) {
        analytics.logEvent(Event.DELETE_ANALYSIS, mapOf(
            Param.TITLE to item.title,
            Param.MEDIA_TYPE to item.mediaType.name
        ))
        val updatedHistory = _uiState.value.history.filter { it.id != item.id }
        _uiState.update { it.copy(history = updatedHistory) }
        settingsStorage.saveHistory(updatedHistory)
    }

    /** Reset only the search-stage state. Active analyses keep running. */
    fun onNewSearch() {
        pendingItem = null
        disambigJob?.cancel()
        disambigJob = null
        _uiState.update {
            it.copy(
                searchQuery = "",
                viewingHistoryItem = null,
                viewingTaskId = null,
                selectedTitle = null,
                showDisambiguation = false,
                disambiguationItems = emptyList(),
                isSearching = false,
                showPaywall = false,
                requiresAuth = false,
                errorMessage = null,
                highlightsLoading = false,
                // Non-signed-in users always use quick check (toggle is hidden); preserve that.
                // Only while the quick/full choice exists at all — with it off, every search is FULL.
                quickCheck = AppConfig.SEARCH_MODE_CHOICE_ENABLED && !SupabaseAuthService.instance.isLoggedIn
            )
        }
        // Showing the review prompt over the search screen, right after the user
        // dismisses the analysis detail (rather than interrupting the result view).
        firePendingReviewPrompt()
        // A book opened from a bulk batch goes back to the batch, not to an empty search screen.
        returnToBulkIfNeeded()
    }

    /** If the user got here by opening a book from a bulk batch, reopen that batch (once). */
    private fun returnToBulkIfNeeded() {
        if (!returnToBulkResults) return
        returnToBulkResults = false
        if (_uiState.value.bulkBatch != null) openBulkScan()
    }

    /** [fromCamera] true = mobile camera scan ("photo_camera"); false = web image upload
     *  ("photo_upload"). Remembered for the candidate-chooser path too. */
    fun onImageSelected(imageBytes: ByteArray, fromCamera: Boolean = true) {
        // Every photo ends in onSearch, which a capped account can't get past; stop here instead
        // so it doesn't also pay for the title extraction and sit through "Processing…" first.
        if (blockedByDailyLimit()) return
        pendingImageSource = if (fromCamera) "photo_camera" else "photo_upload"
        analytics.logEvent(Event.PHOTO_SEARCH, mapOf(Param.SOURCE to pendingImageSource))
        recognizeJob?.cancel()
        _uiState.update { it.copy(isRecognizingText = true, errorMessage = null, imageCandidates = emptyList()) }

        recognizeJob = viewModelScope.launch {
            val candidates = textRecognizer.extract(imageBytes)
            _uiState.update { it.copy(isRecognizingText = false) }

            when {
                candidates.isEmpty() -> _uiState.update {
                    it.copy(errorMessage = "Couldn't find a title in that photo. Try getting closer, filling the frame with the cover, or searching by name.")
                }
                // One clear match — search it straight away (with a clean title, no "Book - " prefix).
                candidates.size == 1 -> {
                    _uiState.update { it.copy(searchQuery = candidates.first().title) }
                    onSearch(pendingImageSource)
                }
                // Several things in the photo — let the user pick which one they meant.
                else -> _uiState.update { it.copy(imageCandidates = candidates) }
            }
        }
    }

    /** User picked one of the photo's candidates — search it (keeps the photo origin). */
    fun onImageCandidateSelected(candidate: MediaCandidate) {
        analytics.logEvent(Event.SELECT_PHOTO_CANDIDATE, mapOf(
            Param.TITLE to candidate.title,
            Param.SOURCE to pendingImageSource
        ))
        _uiState.update { it.copy(imageCandidates = emptyList(), searchQuery = candidate.title) }
        onSearch(pendingImageSource)
    }

    /** User dismissed the photo-candidate chooser without picking. */
    fun dismissImageCandidates() {
        _uiState.update { it.copy(imageCandidates = emptyList()) }
    }

    /** User tapped the X on "Processing…" — abandon a slow photo extraction. */
    fun cancelRecognition() {
        recognizeJob?.cancel()
        recognizeJob = null
        _uiState.update { it.copy(isRecognizingText = false, imageCandidates = emptyList()) }
    }

    // ── Bulk spine scan (library accounts) ──────────────────────────────────────────
    // One photo of a stack or shelf → every book we can read, appearing in a list AS each is identified →
    // the user corrects the list → the ticked books are analysed on the server. Two pieces of state:
    //  * [MediaCheckerUiState.bulkScan]  the screens (null = closed).
    //  * [MediaCheckerUiState.bulkBatch] the running/finished server batch. It outlives the screens: the
    //    user can close them, open a book, or go back to searching, and still get back to the batch from
    //    the main screen until they dismiss it.

    /** Row ids the user has ticked/unticked/edited/removed: later server updates must not undo these. */
    private val bulkTouched = mutableSetOf<String>()
    private val bulkRemoved = mutableSetOf<String>()
    /** Batch books already copied into Your Analyses (by item id). */
    private val bulkHistoryAdded = mutableSetOf<String>()
    /** True when the user opened a book from the batch: closing it returns to the batch, not the search screen. */
    private var returnToBulkResults = false

    /** Whether this account may use bulk scan: the owner switched it on for the account (entitlement bulk_scan) AND it is a library account (holds a search pack). A DEV server that opens bulk scan to everyone (bulk_scan_open) lifts the pack requirement. */
    val bulkScanAvailable: Boolean get() = _entitlement.value?.canUseBulkScan == true // bulkScan && (isLibrary || bulkScanOpen)

    /**
     * Opens the bulk-scan flow. If a batch is running or finished and not dismissed it opens straight on
     * that batch (so the main-screen banner and the menu item both lead back to it); otherwise "pick a photo".
     */
    fun openBulkScan() {
        if (!bulkScanAvailable) return
        val batch = _uiState.value.bulkBatch
        _uiState.update {
            it.copy(bulkScan = if (batch != null) batch.toScreenState() else BulkScanState(BulkScanPhase.PICK))
        }
    }

    private fun BulkBatchState.toScreenState() = BulkScanState(
        phase = BulkScanPhase.RESULTS, batchId = batchId, results = results,
        batchDone = done, offline = offline, error = error
    )

    /** Closes the screens. A running batch is NOT stopped or forgotten: the main screen keeps a way back. */
    fun closeBulkScan() {
        bulkScanJob?.cancel()
        bulkScanJob = null
        _uiState.update { it.copy(bulkScan = null) }
    }

    /** "Scan another stack": back to picking a photo. Any batch keeps running and stays reachable. */
    fun retakeBulkScan() {
        bulkScanJob?.cancel()
        bulkScanJob = null
        bulkTouched.clear(); bulkRemoved.clear()
        _uiState.update { it.copy(bulkScan = BulkScanState(BulkScanPhase.PICK)) }
    }

    /** Forget a FINISHED batch (clears the main-screen banner). Its books stay in Your Analyses. */
    fun dismissBulkBatch() {
        if (_uiState.value.bulkBatch?.done != true) return
        _uiState.update { it.copy(bulkBatch = null) }
    }

    /** The user took or chose a photo: read its spines, showing each book as soon as it is identified. */
    fun onBulkScanPhoto(imageBytes: ByteArray) {
        if (_uiState.value.bulkScan == null) return
        bulkScanJob?.cancel()
        bulkTouched.clear(); bulkRemoved.clear()
        _uiState.update { it.copy(bulkScan = BulkScanState(BulkScanPhase.SCANNING, scanning = true)) }
        bulkScanJob = viewModelScope.launch {
            spineScanner.scanStream(imageBytes).collect { ev ->
                when (ev) {
                    is SpineScanEvent.Found -> updateBulkScan { it.copy(foundCount = ev.count) }
                    is SpineScanEvent.Row -> upsertBulkRow(ev.item)
                    is SpineScanEvent.Merge -> updateBulkScan { b ->
                        b.copy(items = b.items
                            .filterNot { it.id in ev.drop }
                            .map { if (it.id == ev.keep) it.copy(copies = ev.copies) else it })
                    }
                    SpineScanEvent.Done -> updateBulkScan { b ->
                        if (b.items.isEmpty()) b.copy(phase = BulkScanPhase.ERROR, scanning = false,
                            error = "We couldn't see any book spines in that photo. Try again with the spines facing the camera and good light.")
                        else b.copy(phase = BulkScanPhase.REVIEW, scanning = false)
                    }
                    is SpineScanEvent.Failed -> updateBulkScan { b ->
                        // Rows already shown stay usable: the user can still analyse what was found.
                        if (b.items.isEmpty()) b.copy(phase = BulkScanPhase.ERROR, scanning = false, error = ev.message)
                        else b.copy(phase = BulkScanPhase.REVIEW, scanning = false,
                            error = "The scan stopped early, so there may be more books on the shelf. Retake the photo to look again.")
                    }
                }
            }
        }
    }

    private fun updateBulkScan(transform: (BulkScanState) -> BulkScanState) {
        _uiState.update { st -> st.bulkScan?.let { st.copy(bulkScan = transform(it)) } ?: st }
    }

    /**
     * Identity of a book on the review list: its title (without leading articles or punctuation) plus the
     * author's longest name, so spelling and case don't make a repeat look new. Null for a row with no title.
     */
    private fun bulkBookKey(title: String, author: String): String? {
        fun norm(s: String) = s.lowercase().filter { it.isLetterOrDigit() || it == ' ' || it == '+' || it == '#' }
            .split(' ').filter { it.isNotEmpty() }
        val t = norm(title).filter { it != "the" && it != "a" && it != "an" }.joinToString(" ")
        if (t.isEmpty()) return null
        val a = norm(author).filter { w -> w.length >= 3 && w.all { it.isLetter() } }.maxWithOrNull(compareBy({ it.length }, { it })) ?: ""
        return "$t|$a"
    }

    /** Adds a book to the list, or improves the row with the same id; keeps photo order and the user's choices. */
    private fun upsertBulkRow(incoming: BulkScanItem) {
        if (incoming.id in bulkRemoved) return
        updateBulkScan { b ->
            // A book is unique by title AND author together: if another row already is this book, drop the
            // newcomer instead of showing a repeat (the server filters these too; this is the guarantee).
            val key = bulkBookKey(incoming.title, incoming.author)
            if (key != null && b.items.any { it.id != incoming.id && bulkBookKey(it.title, it.author) == key }) {
                bulkRemoved.add(incoming.id)
                return@updateBulkScan b
            }
            val existing = b.items.firstOrNull { it.id == incoming.id }
            val merged = when {
                existing == null -> incoming
                // The user already decided something about this row: their choice wins over a late update.
                incoming.id in bulkTouched -> existing
                else -> incoming
            }
            val items = (b.items.filterNot { it.id == incoming.id } + merged).sortedBy { it.position }
            // The first row arriving is what turns "Reading the spines…" into the list.
            b.copy(items = items, phase = if (b.phase == BulkScanPhase.SCANNING) BulkScanPhase.REVIEW else b.phase)
        }
    }

    /**
     * "Analyze N books": sends the ticked books to the server, which analyses them a few at a time
     * even if this phone locks, then shows the results screen and follows its progress. Each book
     * uses one search from the library's pack. If the photo is still being read, the books found so
     * far are what gets analysed.
     */
    fun startBulkAnalysis() {
        val b = _uiState.value.bulkScan ?: return
        if (b.phase != BulkScanPhase.REVIEW || b.starting) return
        val chosen = b.items.filter { it.selected && it.canAnalyze }
        if (chosen.isEmpty()) return
        // One batch at a time keeps "the batch" a single thing the user can always find.
        if (_uiState.value.bulkBatch?.done == false) {
            updateBulkScan { it.copy(error = "Your other books are still being analyzed. You can start the next stack when they finish.") }
            return
        }
        updateBulkScan { it.copy(starting = true, error = null) }
        viewModelScope.launch {
            val outcome = scanBatchClient.start(chosen, _uiState.value.searchCriteria)
            val failure: String? = when (outcome) {
                is StartBatchOutcome.Started -> null
                is StartBatchOutcome.NotEnoughSearches ->
                    "You have ${outcome.remaining} searches left, but ${outcome.needed} books are ticked. Untick ${outcome.needed - outcome.remaining} and try again."
                StartBatchOutcome.NotLibrary -> "Scanning a stack of books isn't available on your account."
                is StartBatchOutcome.Failed -> outcome.message
            }
            if (outcome is StartBatchOutcome.Started) {
                // The photo scan has nothing more to add once the user has chosen their books.
                bulkScanJob?.cancel(); bulkScanJob = null
                bulkHistoryAdded.clear()
                val batch = BulkBatchState(
                    batchId = outcome.batchId,
                    results = chosen.mapIndexed { i, c ->
                        BulkResultItem(id = "pending$i", title = c.title, author = c.author, year = c.year,
                            coverUrl = c.coverUrl, status = BulkResultStatus.QUEUED, position = i)
                    }
                )
                _uiState.update { it.copy(bulkBatch = batch, bulkScan = batch.toScreenState()) }
                followBulkBatch(outcome.batchId)
            } else {
                updateBulkScan { it.copy(starting = false, error = failure) }
            }
        }
    }

    /** Applies a new batch snapshot to the app state and, if the results screen is open, to the screen. */
    private fun applyBatch(batchId: String, transform: (BulkBatchState) -> BulkBatchState) {
        _uiState.update { st ->
            val cur = st.bulkBatch?.takeIf { it.batchId == batchId } ?: return@update st
            val next = transform(cur)
            val screen = st.bulkScan?.takeIf { it.phase == BulkScanPhase.RESULTS && it.batchId == batchId }
            st.copy(
                bulkBatch = next,
                bulkScan = screen?.copy(results = next.results, batchDone = next.done, offline = next.offline, error = next.error) ?: st.bulkScan
            )
        }
    }

    /** Finished books first (newest finish on top), then the ones being analysed, then waiting, then problems. */
    private fun sortBulkResults(items: List<BulkResultItem>): List<BulkResultItem> {
        fun finishedAt(i: BulkResultItem) = i.updatedAt?.let { runCatching { parseIsoTimestamp(it) }.getOrNull() } ?: 0L
        val done = items.filter { it.status == BulkResultStatus.DONE }.sortedByDescending { finishedAt(it) }
        val running = items.filter { it.status == BulkResultStatus.RUNNING }.sortedBy { it.position }
        val queued = items.filter { it.status == BulkResultStatus.QUEUED }.sortedBy { it.position }
        val problems = items.filter { it.status == BulkResultStatus.FAILED || it.status == BulkResultStatus.REFUSED }.sortedBy { it.position }
        return done + running + queued + problems
    }

    /**
     * Checks the batch every few seconds until it is done or the server forgets it. Runs for as long as the
     * batch is unfinished, whether or not the scan screens are open. Each book that finishes is also copied
     * into Your Analyses, so it shows there like any other analysis.
     */
    private fun followBulkBatch(batchId: String) {
        bulkPollJob?.cancel()
        bulkPollJob = viewModelScope.launch {
            var failures = 0
            while (true) {
                when (val p = scanBatchClient.progress(batchId)) {
                    is BatchProgress.Update -> {
                        failures = 0
                        val sorted = sortBulkResults(p.items)
                        applyBatch(batchId) { it.copy(results = sorted, done = p.done, offline = false) }
                        sorted.filter { it.status == BulkResultStatus.DONE && bulkHistoryAdded.add(it.id) }
                            .forEach { saveBulkResultToHistory(it) }
                        if (p.done) {
                            // Searches were spent: refresh the "Uses N of your X" line.
                            refreshEntitlement()
                            return@launch
                        }
                    }
                    BatchProgress.NotFound -> {
                        applyBatch(batchId) { it.copy(done = true, error = "We lost track of that batch. Anything already analysed is in Your analyses.") }
                        return@launch
                    }
                    BatchProgress.Unreachable -> {
                        failures++
                        applyBatch(batchId) { it.copy(offline = true) }
                    }
                }
                // Back off when the connection is bad (a locked phone comes back to a long gap, not a burst).
                delay(if (failures == 0) 4_000L else minOf(30_000L, 4_000L * (failures + 1)))
            }
        }
    }

    /** Copies a finished batch book into Your Analyses, from the cache (no new analysis, no new charge). */
    private fun saveBulkResultToHistory(item: BulkResultItem) {
        viewModelScope.launch {
            val result = repository.refetchResult(item.title, MediaType.BOOKS, _uiState.value.searchCriteria, item.year) ?: run {
                // Not readable yet (a cache write still landing): allow the next poll to try again.
                bulkHistoryAdded.remove(item.id)
                return@launch
            }
            val displayTitle = (if (!item.year.isNullOrBlank()) "${item.title} (${item.year})" else item.title).sanitizeDisplayTitle()
            val now = currentTimeMillis()
            addToHistory(HistoryItem(
                id = now.toString(), title = displayTitle, mediaType = MediaType.BOOKS,
                result = result, costUSD = 0.0, timestamp = now
            ))
        }
    }

    /**
     * Tap on a finished book: opens its analysis the normal way. It is already in Your Analyses (or in the
     * cache), so this costs nothing more. Closing the analysis brings the user back to the batch.
     */
    fun openBulkResult(item: BulkResultItem) {
        if (item.status != BulkResultStatus.DONE) return
        _uiState.update { it.copy(bulkScan = null) }   // the screens only; the batch stays
        onDisambiguationItemSelected(
            DisambiguationItem(
                title = item.title,
                year = item.year,
                mediaType = MediaType.BOOKS.name,
                coverUrl = item.coverUrl,
                author = item.author.ifBlank { null }
            )
        )
        // Set AFTER the call: starting any analysis clears the flag (see onDisambiguationItemSelected).
        returnToBulkResults = true
    }

    private fun updateBulkItems(transform: (List<BulkScanItem>) -> List<BulkScanItem>) {
        _uiState.update { st ->
            val b = st.bulkScan ?: return@update st
            st.copy(bulkScan = b.copy(items = transform(b.items)))
        }
    }

    fun toggleBulkItem(id: String) {
        bulkTouched.add(id)
        updateBulkItems { items -> items.map { if (it.id == id && it.canAnalyze) it.copy(selected = !it.selected) else it } }
    }

    /** Tick every row that has a title, or clear every tick. */
    fun setAllBulkSelected(selected: Boolean) {
        _uiState.value.bulkScan?.items?.forEach { bulkTouched.add(it.id) }
        updateBulkItems { items -> items.map { if (it.canAnalyze) it.copy(selected = selected) else it } }
    }

    /**
     * The user typed a title (and maybe an author) for a row. A row that had nothing, or only a weak
     * read, becomes a MANUAL row and is ticked; a cleared title unticks it. Cover and year belonged to
     * the old match, so they go.
     */
    fun editBulkItem(id: String, title: String, author: String) {
        bulkTouched.add(id)
        updateBulkItems { items ->
            items.map {
                if (it.id != id) it else {
                    val t = title.trim()
                    it.copy(
                        title = t, author = author.trim(), year = null, coverUrl = null,
                        status = if (t.isEmpty()) it.status else BulkItemStatus.MANUAL,
                        selected = t.isNotEmpty()
                    )
                }
            }
        }
    }

    fun removeBulkItem(id: String) {
        bulkRemoved.add(id)
        updateBulkItems { items -> items.filterNot { it.id == id } }
    }

    /** "Add a book I missed": a typed row, ticked, at the end of the list. */
    fun addBulkItem(title: String, author: String = "") {
        val t = title.trim()
        if (t.isEmpty()) return
        val id = "m${bulkItemCounter++}"
        bulkTouched.add(id)
        updateBulkItems { items ->
            items + BulkScanItem(
                id = id, title = t, author = author.trim(),
                status = BulkItemStatus.MANUAL, selected = true,
                position = (items.maxOfOrNull { it.position } ?: -1) + 1
            )
        }
    }

    /** Parse an ISO-8601 timestamp (e.g. "2026-04-06T10:30:00+00:00") to REAL epoch millis. */
    private fun parseIsoTimestamp(iso: String): Long {
        // Real epoch millis — MUST match currentTimeMillis()'s scale, because locally-created
        // history items are stamped with currentTimeMillis() and this list is sorted by timestamp.
        // The old 30-day-month / no-leap-day approximation ran ~2 weeks behind the real epoch, so a
        // remote-synced item sorted ~2 weeks older than a same-instant locally-created one. Net
        // effect: each device floated its own local analyses above ones synced from the other device,
        // so "Your Analyses" showed a different order on web vs mobile for the same account.
        // Instant.parse handles the Postgres timestamptz form (offset + fractional seconds).
        return try { Instant.parse(iso).toEpochMilliseconds() } catch (_: Exception) { 0L }
    }

    /**
     * Called by the UI after the PIN pad lets a parent out of Kids Mode (and on every sign-in, to
     * retry leftovers): the titles the child checked go into Your Analyses with a 🧒 badge. Their
     * FULL adult analyses are fetched only now — Kids Mode itself never downloads them.
     */
    fun onKidsModeExited() {
        viewModelScope.launch {
            importKidsScans()
            // The child's finished checks are already in the account's history on the server (tagged
            // kids_…): pull them in now, so the parent sees them straight away instead of after a reload.
            loadRemoteHistory()
        }
    }

    private suspend fun importKidsScans() {
        if (kids.state.value.enabled) return
        val pending = kids.pendingImport()
        if (pending.isEmpty()) return
        val imported = mutableListOf<com.islamicmedia.shared.domain.model.KidsScan>()
        val now = currentTimeMillis()
        for (scan in pending.sortedBy { it.startedAt }) {
            // History freezes what it stores (it never re-synthesizes), so wait until the parent's
            // full write-ups have had time to finish — a kid gets their answer at Phase 1, minutes
            // before Phase 2 is done. Retried on every sign-in / foreground until then.
            if (now - scan.startedAt < KIDS_IMPORT_MIN_AGE_MS) continue
            val result = repository.refetchResult(scan.title, scan.mediaType, _uiState.value.searchCriteria, scan.year)
                ?: continue  // not finished / not reachable yet — stays queued for the next sign-in
            val displayTitle = (if (!scan.year.isNullOrBlank()) "${scan.title} (${scan.year})" else scan.title)
                .sanitizeDisplayTitle()
            addToHistory(HistoryItem(
                id = scan.startedAt.toString(),
                title = displayTitle,
                mediaType = scan.mediaType,
                result = result,
                costUSD = 0.0,
                timestamp = scan.startedAt,
                kidScan = true
            ))
            imported += scan
        }
        if (imported.isNotEmpty()) {
            kids.clearImported(imported)
            _uiState.update { s -> s.copy(history = s.history.sortedByDescending { it.timestamp }) }
            settingsStorage.saveHistory(_uiState.value.history)
        }
    }

    private fun addToHistory(item: HistoryItem) {
        // Add to front, dropping any existing entry for the same title. No cap — the remote
        // sync (loadRemoteHistory) loads the user's FULL analysis history, so truncating here
        // would drop synced titles from "Your Analyses" every time a new analysis completes.
        val currentHistory = _uiState.value.history
        val updatedHistory = listOf(item) + currentHistory.filter { it.title != item.title }
        _uiState.update { it.copy(history = updatedHistory) }
        settingsStorage.saveHistory(updatedHistory)
    }

    /** Apply a transform to a single task in `activeAnalyses`. No-op if the id is not present. */
    private fun updateTask(taskId: String, transform: (AnalysisTask) -> AnalysisTask) {
        _uiState.update { state ->
            val task = state.activeAnalyses[taskId] ?: return@update state
            state.copy(activeAnalyses = state.activeAnalyses + (taskId to transform(task)))
        }
    }

    /** Bump a task's progress fraction monotonically. */
    private fun setTaskProgress(taskId: String, fraction: Float) {
        updateTask(taskId) { task ->
            if (fraction > task.maxProgressFraction) {
                task.copy(progressFraction = fraction, maxProgressFraction = fraction)
            } else task
        }
    }

    private fun progressToStep(progress: AnalysisProgress, total: Int): Triple<Int, Int, String> {
        return when (progress) {
            is AnalysisProgress.Searching -> Triple(1, total, "Searching")
            is AnalysisProgress.Summarizing -> Triple(2, total, "Processing")
            is AnalysisProgress.ContentCacheHit -> Triple(2, total, "Cached")
            is AnalysisProgress.Analyzing -> {
                Triple(3, progress.totalConcerns + 4, "Evaluating")
            }
            is AnalysisProgress.ConcernComplete -> {
                val step = 2 + progress.concernIndex // concerns are steps 3..3+N
                val name = if (progress.concernName.isNotBlank()) progress.concernName else "Checking..."
                Triple(step, total, name)
            }
            is AnalysisProgress.CrossReferencing -> Triple(total - 1, total, "Finalizing")
            is AnalysisProgress.CompilingReport -> Triple(total - 1, total, "Finalizing")
            is AnalysisProgress.EnrichingWithGuidance -> Triple(total - 1, total, "Finalizing")
            is AnalysisProgress.AnalysisComplete -> Triple(total, total, "Done!")
            else -> Triple(0, total, "")
        }
    }

    /**
     * Dispatch a progress event. `taskId` is null only for search-stage events
     * (Disambiguating, DisambiguationComplete) which mutate the flat ui state. All
     * analysis events require a non-null taskId, identifying which task to mutate.
     */
    private fun handleProgress(progress: AnalysisProgress, taskId: String?) {
        when (progress) {
            is AnalysisProgress.Disambiguating -> {
                _uiState.update { it.copy(isSearching = true) }
            }
            is AnalysisProgress.DisambiguationComplete -> {
                lastDisambigJourneyId = progress.journeyId
                analytics.logEvent(Event.VIEW_SEARCH_RESULTS, mapOf(
                    "search_term" to _uiState.value.searchQuery,
                    "item_count" to progress.items.size
                ))
                _uiState.update {
                    it.copy(
                        isSearching = false,
                        disambiguationItems = progress.items,
                        showDisambiguation = progress.items.isNotEmpty()
                    )
                }
                if (progress.items.isEmpty()) {
                    _uiState.update {
                        it.copy(errorMessage = "No results found. Try a different search term.")
                    }
                }
            }
            is AnalysisProgress.Searching -> {
                if (taskId == null) return
                setTaskProgress(taskId, (progress.stepNumber * 0.04f).coerceAtMost(0.22f))
                updateTask(taskId) { task ->
                    val newStep = ProgressStep(
                        stepNumber = progress.stepNumber,
                        query = progress.query,
                        status = ProgressStatus.SEARCHING
                    )
                    task.copy(progressSteps = task.progressSteps + newStep)
                }
            }
            is AnalysisProgress.SearchComplete -> {
                if (taskId == null) return
                updateTask(taskId) { task ->
                    val updated = task.progressSteps.map { step ->
                        if (step.stepNumber == progress.stepNumber) {
                            step.copy(
                                status = ProgressStatus.COMPLETED,
                                durationMs = progress.durationMs,
                                costUSD = progress.costUSD
                            )
                        } else step
                    }
                    task.copy(
                        progressSteps = updated,
                        costUSD = task.costUSD + progress.costUSD
                    )
                }
            }
            is AnalysisProgress.Summarizing -> {
                if (taskId == null) return
                setTaskProgress(taskId, 0.25f)
                updateTask(taskId) { task ->
                    val summarizeStep = ProgressStep(
                        stepNumber = task.progressSteps.size + 1,
                        query = "Condensing research findings...",
                        status = ProgressStatus.SEARCHING
                    )
                    task.copy(progressSteps = task.progressSteps + summarizeStep)
                }
            }
            is AnalysisProgress.ContentCacheHit -> {
                if (taskId == null) return
                setTaskProgress(taskId, 0.30f)
                updateTask(taskId) { task ->
                    val cacheStep = ProgressStep(
                        stepNumber = 1,
                        query = "Research data found in cache, skipping search...",
                        status = ProgressStatus.COMPLETED
                    )
                    task.copy(
                        progressSteps = task.progressSteps + cacheStep,
                        wasCacheHit = true
                    )
                }
            }
            is AnalysisProgress.AnalysisCacheHit -> {
                if (taskId == null) return
                setTaskProgress(taskId, 0.30f)
                updateTask(taskId) { task ->
                    val cacheStep = ProgressStep(
                        stepNumber = 1,
                        query = "Previous analysis found, loading results...",
                        status = ProgressStatus.COMPLETED
                    )
                    task.copy(
                        progressSteps = task.progressSteps + cacheStep,
                        wasCacheHit = true
                    )
                }
            }
            is AnalysisProgress.Analyzing -> {
                if (taskId == null) return
                setTaskProgress(taskId, 0.30f)
                updateTask(taskId) { task ->
                    val updated = task.progressSteps.map { step ->
                        if (step.query == "Condensing research findings..." && step.status == ProgressStatus.SEARCHING) {
                            step.copy(status = ProgressStatus.COMPLETED)
                        } else step
                    }
                    val newStep = ProgressStep(
                        stepNumber = task.progressSteps.size + 1,
                        query = "Evaluating ${progress.totalConcerns} concerns...",
                        status = ProgressStatus.SEARCHING
                    )
                    task.copy(
                        progressSteps = updated + newStep,
                        totalConcerns = progress.totalConcerns
                    )
                }
            }
            is AnalysisProgress.ConcernComplete -> {
                if (taskId == null) return
                val fraction = 0.30f + (progress.concernIndex.toFloat() / progress.totalConcerns.toFloat()) * 0.55f
                setTaskProgress(taskId, fraction)
                updateTask(taskId) { task ->
                    val updated = task.progressSteps.map { step ->
                        if (step.status == ProgressStatus.SEARCHING && step.query.contains("concern", ignoreCase = true)) {
                            step.copy(status = ProgressStatus.COMPLETED)
                        } else step
                    }
                    val name = if (progress.concernName.isNotBlank()) progress.concernName else "Concern ${progress.concernIndex}"
                    val newStep = ProgressStep(
                        stepNumber = updated.size + 1,
                        query = "$name (${progress.concernIndex}/${progress.totalConcerns})",
                        status = if (progress.concernIndex < progress.totalConcerns) ProgressStatus.SEARCHING else ProgressStatus.COMPLETED
                    )
                    task.copy(progressSteps = updated + newStep)
                }
            }
            is AnalysisProgress.CrossReferencing -> {
                if (taskId == null) return
                setTaskProgress(taskId, 0.87f)
                updateTask(taskId) { task ->
                    val updated = task.progressSteps.map { step ->
                        if (step.status == ProgressStatus.SEARCHING) step.copy(status = ProgressStatus.COMPLETED) else step
                    }
                    val newStep = ProgressStep(
                        stepNumber = task.progressSteps.size + 1,
                        query = "Cross-referencing findings...",
                        status = ProgressStatus.SEARCHING
                    )
                    task.copy(progressSteps = updated + newStep)
                }
            }
            is AnalysisProgress.CompilingReport -> {
                if (taskId == null) return
                setTaskProgress(taskId, 0.92f)
                updateTask(taskId) { task ->
                    val updated = task.progressSteps.map { step ->
                        if (step.query == "Cross-referencing findings..." && step.status == ProgressStatus.SEARCHING) {
                            step.copy(status = ProgressStatus.COMPLETED)
                        } else step
                    }
                    val newStep = ProgressStep(
                        stepNumber = task.progressSteps.size + 1,
                        query = "Compiling report...",
                        status = ProgressStatus.SEARCHING
                    )
                    task.copy(progressSteps = updated + newStep)
                }
            }
            is AnalysisProgress.EnrichingWithGuidance -> {
                if (taskId == null) return
                setTaskProgress(taskId, 0.96f)
                updateTask(taskId) { task ->
                    val updated = task.progressSteps.map { step ->
                        if (step.query == "Compiling report..." && step.status == ProgressStatus.SEARCHING) {
                            step.copy(status = ProgressStatus.COMPLETED)
                        } else step
                    }
                    val newStep = ProgressStep(
                        stepNumber = task.progressSteps.size + 1,
                        query = "Adding age-appropriate guidance...",
                        status = ProgressStatus.SEARCHING
                    )
                    task.copy(progressSteps = updated + newStep)
                }
            }
            is AnalysisProgress.AnalysisComplete -> {
                if (taskId == null) return
                setTaskProgress(taskId, 1.0f)
                val task = _uiState.value.activeAnalyses[taskId] ?: return
                // The stream watchdog (and a late duplicate event) can deliver AnalysisComplete for
                // a task that already completed. Such a repeat must not log a second
                // complete_analysis or count as another analysis towards the review prompt.
                val wasAlreadyComplete = task.status == TaskStatus.COMPLETE
                val durationSeconds = ((currentTimeMillis() - task.startedAtMs) / 1000).toInt()
                if (!wasAlreadyComplete) {
                    analytics.logEvent(Event.COMPLETE_ANALYSIS, mapOf(
                        Param.TITLE to task.displayTitle,
                        Param.MEDIA_TYPE to task.mediaType.name,
                        Param.SCORE to progress.result.score,
                        "duration_seconds" to durationSeconds,
                        "cache_hit" to task.wasCacheHit,
                        Param.QUICK_CHECK to task.quickCheck
                    ))
                }
                val nowMs = currentTimeMillis()
                updateTask(taskId) { t ->
                    val updated = t.progressSteps.map { step ->
                        if (step.status == ProgressStatus.SEARCHING) step.copy(status = ProgressStatus.COMPLETED) else step
                    }
                    t.copy(
                        progressSteps = updated,
                        result = progress.result,
                        costUSD = t.costUSD + progress.costUSD,
                        status = TaskStatus.COMPLETE,
                        completedAtMs = nowMs
                    )
                }
                // Load similar/alternative titles for the just-finished result (live, non-blocking).
                // Skipped when the streamed Phase 1 verdict already kicked these off for this same
                // title — alternatives discovery is a paid server-side call, so don't pay twice.
                if (task.phase1PartialResult?.analysisPending != true) {
                    loadAlternativesFor(task.title, task.mediaType, task.item.year, progress.result.author)
                    loadCommentsFor(task.title, task.mediaType, task.item.year)
                }
                // Re-analyzing a hidden title un-hides it — that's the documented Hide contract.
                clearHiddenFor(task.displayTitle, task.mediaType)
                // Persist to history
                val sanitized = task.displayTitle.sanitizeDisplayTitle()
                val historyItem = HistoryItem(
                    id = nowMs.toString(),
                    title = sanitized,
                    mediaType = task.mediaType,
                    result = progress.result,
                    costUSD = task.costUSD + progress.costUSD,
                    timestamp = nowMs
                )
                addToHistory(historyItem)
                // First-ever analysis of a title has no highlights yet: the server generates them
                // (Haiku) fire-and-forget AFTER `complete`. Poll the criteria-independent Phase 1
                // cache for a few seconds and patch the tldr in-place so the user sees highlights
                // on THIS search, not the next one. Full mode only (quick results never get tldr).
                if (!progress.result.quickCheck && progress.result.tldr.isNullOrEmpty()) {
                    backfillTldrForCompletedTask(taskId, task.title, task.mediaType, task.item.year, historyItem.id)
                } else {
                    _uiState.update { it.copy(highlightsLoading = false) }
                }
                // Store review request: after EVERY 5th completed analysis (ReviewPromptRule), mobile
                // only, never in Kids Mode. Count each real completion once (not the watchdog's
                // repeat). Don't ask now — queue it so the OS card appears only AFTER the user
                // closes this analysis detail (see firePendingReviewPrompt).
                if (!wasAlreadyComplete && platformName != "Web" && !kids.state.value.enabled) {
                    val previousCount = settingsStorage.loadAnalysisCount()
                    // First time on this install: seed from the count BEFORE this analysis (the last
                    // multiple of 5 already passed), so someone updating with 7 analyses is not
                    // prompted at once (next prompt: 10) while someone on 4 still gets one at 5.
                    val lastPromptAt = settingsStorage.loadLastReviewPromptAt()
                        ?: ReviewPromptRule.seedLastPromptAt(previousCount)
                            .also { settingsStorage.saveLastReviewPromptAt(it) }
                    val newCount = previousCount + 1
                    settingsStorage.saveAnalysisCount(newCount)
                    if (ReviewPromptRule.shouldPrompt(newCount, lastPromptAt)) {
                        reviewPromptPending = true
                    }
                }
            }
            is AnalysisProgress.Error -> {
                if (taskId == null) return
                val task = _uiState.value.activeAnalyses[taskId]
                analytics.logEvent(Event.ANALYSIS_FAILED, mapOf(
                    "title" to (task?.displayTitle ?: ""),
                    "error_message" to progress.message.take(100)
                ))
                val msg = progress.message

                // Billing backstop: server rejected for lack of credits (or auth). Surface the
                // paywall / sign-in instead of a scary error, and refresh balances. The proactive
                // gate normally catches this first; this covers stale-state races.
                if (AppConfig.PAYMENTS_ENABLED && (msg.contains("no_credits") || msg.contains("402"))) {
                    refreshEntitlement()
                    if (_entitlement.value?.isLibrary == true) {
                        _uiState.update { it.copy(showPaywall = false, showLibraryOutOfSearches = true) }
                        updateTask(taskId) { it.copy(status = TaskStatus.CANCELLED, errorMessage = null, internalError = null) }
                        return
                    }
                    analytics.logEvent(Event.PAYWALL_SHOWN, mapOf(Param.SOURCE to "server_rejected"))
                    _uiState.update { it.copy(showPaywall = true) }
                    updateTask(taskId) { it.copy(status = TaskStatus.CANCELLED, paywallBlocked = true, errorMessage = null, internalError = null) }
                    return
                }
                if (AppConfig.PAYMENTS_ENABLED && (msg.contains("auth_required") || msg.contains("401"))) {
                    _uiState.update { it.copy(requiresAuth = true) }
                    updateTask(taskId) { it.copy(status = TaskStatus.CANCELLED, authBlocked = true, errorMessage = null, internalError = null) }
                    return
                }

                val isRateLimited = msg.contains("429") ||
                    msg.contains("rate limit", ignoreCase = true) ||
                    msg.contains("Too Many Requests", ignoreCase = true) ||
                    msg.contains("reached your limit", ignoreCase = true)

                val isUserActionable = isRateLimited ||
                    msg.contains("session has expired", ignoreCase = true) ||
                    msg.contains("sign out and sign back in", ignoreCase = true) ||
                    msg.contains("no internet", ignoreCase = true) ||
                    msg.contains("timed out", ignoreCase = true) ||
                    msg.contains("try again", ignoreCase = true) ||
                    msg.contains("not enough information", ignoreCase = true) ||
                    msg.contains("temporarily unavailable", ignoreCase = true)

                updateTask(taskId) {
                    it.copy(
                        status = TaskStatus.ERROR,
                        progressFraction = 0f,
                        maxProgressFraction = 0f,
                        errorMessage = if (isUserActionable) msg
                            else "Something went wrong with our analysis. Help us fix this by letting us know what happened.",
                        internalError = if (isUserActionable) null else msg
                    )
                }
                // Mirror onto top-level errorMessage for the existing error UI when this task is being viewed.
                if (_uiState.value.viewingTaskId == taskId) {
                    _uiState.update {
                        it.copy(
                            errorMessage = if (isUserActionable) msg
                                else "Something went wrong with our analysis. Help us fix this by letting us know what happened.",
                            internalError = if (isUserActionable) null else msg
                        )
                    }
                }
            }
            is AnalysisProgress.DailyLimitReached -> {
                if (taskId == null) return
                // The server refused to START this run: nothing was charged or counted, so there is
                // no result and nothing worth retrying. Drop the task the way a user cancel does
                // (no failed tile left in Your Analyses, nobody stranded on the progress view) and
                // explain with the dialog instead. No /api/cancel: there is no pipeline to stop.
                // collectAnalysisFlow's own recovery (auto-retry, /api/resume) only ever fires on
                // AnalysisProgress.Error, so it can't pick this up.
                val state = _uiState.value
                analytics.logEvent(Event.DAILY_LIMIT_REACHED, mapOf(Param.SOURCE to "server"))
                settingsStorage.clearPendingAnalysis()
                if (state.runningAnalysisCount <= 1) {
                    backgroundManager.endLiveActivity(false)
                    backgroundManager.stopBackgroundExecution()
                    backgroundManager.dismissNotifications()
                } else {
                    // The Live Activity is single-track and follows the most recently STARTED task.
                    // If that's this refused one, end it now; left up, the other run's completion
                    // would later end it as "complete" under this title.
                    val refused = state.activeAnalyses[taskId]
                    val newerStart = refused != null && state.activeAnalyses.values.any {
                        it.id != taskId && it.startedAtMs > refused.startedAtMs
                    }
                    if (refused != null && !newerStart) backgroundManager.endLiveActivity(false)
                }
                // Usually cancels the very coroutine we're running in (collectAnalysisFlow); the rest
                // of this branch doesn't suspend, and the stream is already over.
                activeJobs.remove(taskId)?.cancel()
                _uiState.update {
                    val viewing = it.viewingTaskId == taskId
                    it.copy(
                        activeAnalyses = it.activeAnalyses - taskId,
                        viewingTaskId = if (viewing) null else it.viewingTaskId,
                        selectedTitle = if (viewing) null else it.selectedTitle,
                        dailyLimitNotice = progress.notice
                    )
                }
                // Pull the fresh daily_used / daily_resets_at so the pre-flight catches the next
                // attempt before its paid search.
                refreshEntitlement()
            }
            is AnalysisProgress.Cancelled -> {
                if (taskId == null) return
                updateTask(taskId) {
                    it.copy(
                        status = TaskStatus.CANCELLED,
                        progressFraction = 0f,
                        maxProgressFraction = 0f
                    )
                }
            }
            is AnalysisProgress.Phase1Complete -> {
                if (taskId == null) return
                setTaskProgress(taskId, 0.88f)
                // V2: Phase 1 verdict ready. Store the partial AnalysisResult so UI can render
                // the verdict early in Full mode. AnalysisComplete will follow with the full
                // enriched result (or in Quick mode, the same data).
                updateTask(taskId) { it.copy(phase1PartialResult = progress.partialResult) }

                // The results card is about to open on this partial, so the title-scoped live
                // sections must be re-pointed NOW. Both loaders clear their state up front, so this
                // also evicts the previously-viewed title's alternatives/comments — which would
                // otherwise sit under this result for the whole of Phase 2.
                if (progress.partialResult.analysisPending) {
                    val task = _uiState.value.activeAnalyses[taskId]
                    if (task != null) {
                        loadAlternativesFor(task.title, task.mediaType, task.item.year, progress.partialResult.author)
                        loadCommentsFor(task.title, task.mediaType, task.item.year)
                    }
                }
            }
            is AnalysisProgress.Phase2Started -> {
                if (taskId == null) return
                updateTask(taskId) {
                    it.copy(
                        phase2UnitsTotal = progress.total,
                        phase2UnitsDone = progress.alreadyDone,
                    )
                }
            }
            is AnalysisProgress.ConcernEnriched -> {
                // V2: a single Phase 2 unit completed. Advance the "N of M" count and, for a concern
                // write-up, fetch it so its card fills in now rather than when the whole analysis
                // completes. verdict_meta /
                // developmental arrive under this same event; the server's total counts them too.
                if (taskId == null) return
                updateTask(taskId) {
                    it.copy(
                        phase2UnitsDone = (it.phase2UnitsDone + 1).coerceAtMost(it.phase2UnitsTotal),
                    )
                }
                if (progress.unit.startsWith("concern:") || progress.unit.startsWith("custom:")) {
                    requestPartialRefresh(taskId)
                }
            }
            is AnalysisProgress.TldrReady -> {
                if (taskId == null) return
                updateTask(taskId) { task ->
                    val partial = task.phase1PartialResult
                    if (partial == null || !partial.tldr.isNullOrEmpty()) task
                    else task.copy(phase1PartialResult = partial.copy(tldr = progress.points))
                }
            }
            is AnalysisProgress.Heartbeat -> {
                // Pure liveness — the caller stamps lastEventAtMs and returns before reaching here.
                // Present so this `when` stays exhaustive (and thus keeps failing the build if a
                // future progress type is added without a home).
            }
            is AnalysisProgress.PartialComplete -> {
                if (taskId == null) return
                // V2: Phase 2 done but with failures. Surface the failed unit list to the UI
                // so it can render retry icons for those cards.
                updateTask(taskId) { it.copy(failedPhase2Units = progress.failedUnits) }
            }
        }
    }
}

/**
 * Shown to a library / school account (see Entitlement.isLibrary) that has used every search it was
 * granted, in place of the subscription paywall: it cannot subscribe, it asks us for more.
 */
private const val LIBRARY_OUT_OF_SEARCHES_MESSAGE =
    "You've used all your searches. To add more, please email support@fitrahfilter.com."
